import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl, thumbUrl } from "../lib/api";
import { useGenerations } from "../lib/generations";
import Sidebar from "../components/Sidebar";
import PatternSelector from "../components/PatternSelector";
import ReferencePicker from "../components/ReferencePicker";
import MentionAutocomplete from "../components/MentionAutocomplete";
import ChatsDrawer from "../components/ChatsDrawer";
import ConsumptionBadges from "../components/ConsumptionBadges";
import ZoneEditor from "../components/ZoneEditor";
import HowItWorksModal from "../components/HowItWorksModal";
import RetryImage from "../components/RetryImage";
import RatingButtons from "../components/RatingButtons";
import { useMyRatings } from "../lib/useMyRatings";
import { useCurrencyPref } from "./admin/_shared";
import {
    Send,
    Image as ImageIcon,
    Sliders,
    Sparkles,
    Copy,
    Heart,
    Maximize2,
    X,
    ChevronDown,
    ChevronUp,
    Paperclip,
    PanelRightOpen,
    PanelRightClose,
    Pencil,
    HelpCircle,
    Download,
    RefreshCw,
    Settings,
} from "lucide-react";
import { toast } from "sonner";

const DEFAULT_SETTINGS = {
    model_id: "",
    width: 1024,
    height: 1024,
    num_images: 1,
    patterns: [],
    reference_image_ids: [],
    reference_urls: [],
    use_brand_context: true,
    // Default starts at "low" so generations hug the reference unless
    // the user explicitly dials creativity up.
    creativity_level: "low",
    // Quality preset — admin-configurable per platform. Multiplies the
    // resolution server-side and adds an admin-defined USD surcharge.
    quality: "basic",
};

const QUALITY_OPTIONS = [
    { value: "basic", label: "Básico", scale: 1.0, hint: "Resolución base" },
    { value: "medium", label: "Moderado", scale: 1.5, hint: "+50% px por lado" },
    { value: "high", label: "Súper", scale: 2.0, hint: "+100% px por lado" },
];

const CREATIVITY_OPTIONS = [
    {
        value: "low",
        label: "Poco creativo",
        desc: "Muy apegado a la referencia",
    },
    {
        value: "medium",
        label: "Medio creativo",
        desc: "Se permite salir un poco del concepto",
    },
    {
        value: "high",
        label: "Muy creativo",
        desc: "La referencia es sólo inspiración",
    },
];

export default function Chat() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [chat, setChat] = useState(null);
    const [messages, setMessages] = useState([]);
    // Per-user ratings cache for image library items shown in this chat.
    // Each `GeneratedImage` reads its initial state from here so the
    // thumbs-up/down buttons render in the right state on mount.
    const myRatings = useMyRatings("image");
    const [models, setModels] = useState([]);
    const [resolutions, setResolutions] = useState([]);
    const [patterns, setPatterns] = useState(null);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [configOpen, setConfigOpen] = useState(false);
    // Refs-strip auto-collapses after the user hits Generar (so the freshly
    // generated image gets maximum vertical real-estate) but they can
    // expand it again manually any time.
    const [refsCollapsed, setRefsCollapsed] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(true);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [refThumbs, setRefThumbs] = useState([]);
    // Standalone resize flow (button next to the composer). The user picks
    // ONE source image via the existing picker, then the ResizeDialog opens
    // in "library" mode and writes directly to /library (no chat message).
    const [resizePickerOpen, setResizePickerOpen] = useState(false);
    const [standaloneResize, setStandaloneResize] = useState(null);
    // Apoyos: ad-hoc images uploaded directly from the chat compose area
    // (distinct from `refThumbs`, which are brought in from the Referencias
    // portfolio via the ReferencePicker). Both end up inside
    // `settings.reference_image_ids` on send — referencias go FIRST so the
    // model treats them as the base, apoyos follow as style cues.
    const [apoyoThumbs, setApoyoThumbs] = useState([]);
    const [uploadingApoyo, setUploadingApoyo] = useState(false);
    // 0..100 — aggregate upload progress while one or more apoyo files
    // are uploading. Drives the visual fill of the Generar button and
    // the green outline of the paperclip preview so the user gets clear
    // feedback that the files haven't fully landed yet. When no upload
    // is in flight this stays at 0 and the Generar button works as
    // usual. When uploading the button is disabled until 100%.
    const [apoyoProgress, setApoyoProgress] = useState(0);
    const apoyoInputRef = useRef(null);
    const textareaRef = useRef(null);
    // `@mention` autocomplete for the chat composer. When the user is
    // typing a `@partial` token we open a popover of matching own-refs
    // (see MentionAutocomplete.jsx). Selecting a suggestion inserts the
    // slug into the textarea AND immediately attaches the reference so
    // the generation sees it as an input image, just like drag-and-drop.
    const [mention, setMention] = useState({
        active: false,
        query: "",
        tokenStart: -1, // char index of the `@` sign in `text`
        items: [],
        activeIdx: 0,
        loading: false,
    });
    // Drag-and-drop overlay state. We track a counter (not just a
    // bool) because dragenter / dragleave fire on every child element
    // as the mouse moves — incrementing on enter and decrementing on
    // leave keeps the overlay visible until the drag really leaves
    // the chat surface.
    const [dragDepth, setDragDepth] = useState(0);
    const dragActive = dragDepth > 0;
    const [helpOpen, setHelpOpen] = useState(false);
    const scrollerRef = useRef(null);
    // Currency + TRM for displaying per-model price next to its name in the
    // dropdown. Reads /credits and follows the user's per-user toggle.
    const [backendCurrency, setBackendCurrency] = useState("USD");
    const [currency] = useCurrencyPref(backendCurrency);
    const [trm, setTrm] = useState(4000);
    // Platform-wide chat defaults from /credits: quality surcharges (USD per
    // tier), default quality preset, default resolution and the toggle that
    // shows/hides the construction-pattern selector. Admin manages all of
    // these from /admin/configuracion.
    const [qualitySurchargeUsd, setQualitySurchargeUsd] = useState({
        basic: 0,
        medium: 0.5,
        high: 0.7,
    });
    const [showPatternsInChat, setShowPatternsInChat] = useState(true);
    // USD added per "extra" reference beyond the first of each kind.
    // Default 0.10 — admin overridable from /admin/configuracion.
    const [extraRefCostUsd, setExtraRefCostUsd] = useState(0.1);

    useEffect(() => {
        api.get("/credits").then((r) => {
            setBackendCurrency(r.data.currency || "USD");
            setTrm(Number(r.data.trm_cop_per_usd || 4000));
            if (r.data.quality_surcharge_usd) {
                setQualitySurchargeUsd({
                    basic: Number(r.data.quality_surcharge_usd.basic ?? 0),
                    medium: Number(r.data.quality_surcharge_usd.medium ?? 0.5),
                    high: Number(r.data.quality_surcharge_usd.high ?? 0.7),
                });
            }
            if (typeof r.data.show_patterns_in_chat === "boolean") {
                setShowPatternsInChat(r.data.show_patterns_in_chat);
            }
            if (typeof r.data.extra_ref_cost_usd === "number") {
                setExtraRefCostUsd(r.data.extra_ref_cost_usd);
            }
            // Hydrate the default quality preset only when the user hasn't
            // touched it yet (settings is still on factory defaults).
            const dq = r.data.default_quality;
            if (dq && ["basic", "medium", "high"].includes(dq)) {
                setSettings((s) =>
                    s.quality && s.quality !== "basic" ? s : { ...s, quality: dq },
                );
            }
        }).catch(() => {});
    }, []);

    // Pre-warm the reference picker cache on chat mount so the first time the
    // user clicks "Traer desde /referencias" the modal paints instantly
    // instead of waiting for 3 blocking HTTP round-trips.
    useEffect(() => {
        const preload = async (key, url, transform) => {
            try {
                const r = await api.get(url);
                const data = transform ? transform(r.data) : r.data;
                sessionStorage.setItem(
                    `kc:picker-cache:${key}`,
                    JSON.stringify(data ?? []),
                );
            } catch {
                /* ignore — picker falls back to live fetch */
            }
        };
        // Stagger slightly so we don't block the chat's own initial fetches.
        // Library is paged at 24 in the picker, so we only pre-warm page 1
        // under the same cache key the picker reads from (`library:p1`).
        const t = setTimeout(() => {
            preload("own", "/references?category=own");
            preload("external", "/references?category=external");
            preload("library:p1", "/library?skip=0&limit=24", (d) => ({
                items: d.items || [],
                total: d.total || 0,
            }));
            preload("brand", "/brand/logos");
            preload("carousels", "/references/carousels");
        }, 800);
        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        api.get("/catalog/models").then((r) => {
            setModels(r.data);
            // Pick the default model in this order:
            //   1. The model the super admin marked `is_default` from
            //      /admin/modelos (must still be enabled).
            //   2. Nano Banana Pro (i2i) — historical default, best
            //      quality/cost balance for brand-aware generation.
            //   3. The cheapest enabled model overall.
            //   4. The first model in the catalog.
            const list = r.data || [];
            const adminDefault = list.find(
                (m) => m.is_default && m.enabled !== false,
            );
            const nanoBanana = list.find(
                (m) =>
                    m.id === "fal-ai/nano-banana-pro/edit" && m.enabled !== false,
            );
            const cheapest = [...list]
                .filter((m) => (m.cost_per_image_cop ?? null) !== null)
                .sort(
                    (a, b) =>
                        (a.cost_per_image_cop || 0) -
                        (b.cost_per_image_cop || 0),
                )[0];
            const defaultId =
                adminDefault?.id || nanoBanana?.id || cheapest?.id || list[0]?.id || "";
            setSettings((s) => ({
                ...s,
                model_id: s.model_id || defaultId,
            }));
        });
        api.get("/catalog/resolutions").then((r) => setResolutions(r.data));
        // Default resolution comes from the admin config. We apply it ONLY
        // when the user hasn't already picked one — i.e. settings still on
        // the 1024×1024 factory default.
        api.get("/credits").then((r) => {
            const defaultId = r.data?.default_resolution_id;
            if (!defaultId) return;
            api.get("/catalog/resolutions").then((rr) => {
                const match = (rr.data || []).find((x) => x.id === defaultId);
                if (!match) return;
                setSettings((s) =>
                    s.width === 1024 && s.height === 1024
                        ? { ...s, width: match.width, height: match.height }
                        : s,
                );
            });
        }).catch(() => {});
        api.get("/catalog/patterns").then((r) => setPatterns(r.data));
    }, []);

    /**
     * The /catalog/patterns endpoint returns a category-grouped dict
     * (`{"FLUJO DE LECTURA": [...], "ESTRUCTURAL": [...]}`) because the
     * pattern picker renders sections. The MessageBubble badges, however,
     * need a FLAT list to do `.find(p => p.id === pid)` for label lookup.
     * We flatten once here so both consumers stay happy without each
     * re-running the same loop on every render.
     *
     * Also defends against the response coming through as something other
     * than an object/array (legacy data, race condition before fetch lands).
     */
    const flatPatterns = (() => {
        if (Array.isArray(patterns)) return patterns;
        if (patterns && typeof patterns === "object") {
            return Object.values(patterns).flat().filter(Boolean);
        }
        return [];
    })();

    /**
     * Cross-chat generation tracking.
     *
     * Why we replaced the per-chat polling loop:
     *   - <Chat/> is remounted with a fresh `key` every time the user
     *     navigates between chats (see ChatRoute in App.js). The old
     *     polling effect cancelled on unmount, so a generation in chat A
     *     that completed while the user was in chat B never had anyone
     *     listening — when they came back, the message would sit in
     *     "generating" until the next full reload (or forever).
     *   - Now polling lives in `GenerationsProvider` at the App root and
     *     keeps ticking regardless of route. This component just:
     *       (1) registers any `generating` messages it sees in tracking,
     *       (2) listens for `kc:msg-updated` events and merges the
     *           latest message body into local state.
     */
    const { track: trackGeneration } = useGenerations();

    // Effective chat id — prefer `chat.id` from state because the URL
    // is updated via `window.history.replaceState` after lazy chat
    // creation, and React Router's `useParams()` doesn't observe that.
    // Without this fallback the effects below would sit on `id=undefined`
    // for the first-ever message of a chat, so `kc:msg-updated` events
    // fired by the global polling loop would land with no listener —
    // the message would stay stuck in `generating` locally until the
    // user refreshed.
    const activeChatId = chat?.id || id;

    useEffect(() => {
        if (!activeChatId) return;
        // Register every assistant message currently in `generating` so
        // the global polling loop monitors them. Idempotent: track()
        // ignores messages already in its set.
        for (const m of messages) {
            if (m.role === "assistant" && m.status === "generating") {
                trackGeneration(activeChatId, m.id);
            }
        }
    }, [messages, activeChatId, trackGeneration]);

    useEffect(() => {
        if (!activeChatId) return;
        const onUpdate = (e) => {
            const { chatId, msgId, msg } = e.detail || {};
            // Only merge updates that belong to THIS chat. Updates for
            // other chats are picked up by their own <Chat/> when the
            // user navigates back (initial GET /chats/:id returns the
            // latest doc).
            if (chatId !== activeChatId || !msg) return;
            setMessages((prev) =>
                prev.map((m) => (m.id === msgId ? msg : m)),
            );
        };
        window.addEventListener("kc:msg-updated", onUpdate);
        return () => window.removeEventListener("kc:msg-updated", onUpdate);
    }, [activeChatId]);

    useEffect(() => {
        if (!id) {
            // No id in URL → show empty composer. We intentionally do NOT
            // create a chat here, to avoid littering the history with empty
            // chats when the user only clicked "Nuevo chat". The chat record
            // is created on the first message send (see `send`).
            setChat(null);
            setMessages([]);
            return;
        }
        const fullReload = () => {
            api.get(`/chats/${id}`)
                .then((r) => {
                    setChat(r.data.chat);
                    setMessages(r.data.messages);
                })
                .catch(() => {
                    toast.error("Chat no encontrado");
                    navigate("/");
                });
        };
        fullReload();
        // When the tab regains visibility (user comes back after switching
        // away), browsers throttle/pause timers so the polling loop may
        // have been stalled for minutes. Force a full reload to pick up
        // any state the backend already finished — this also triggers the
        // server-side stale-cleanup that flips long-stuck `generating`
        // messages to `failed` so the user sees a clear error instead of
        // an infinite progress bar.
        const onVisible = () => {
            if (document.visibilityState === "visible") fullReload();
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [id, navigate]);

    // Auto-scroll the message list to the bottom whenever the count
    // changes (initial load OR a new turn appended) so the user always
    // lands on the most recent activity — not at the top of the chat.
    // We schedule the scroll on the NEXT animation frame so the DOM has
    // committed the new nodes (and images have reserved their layout
    // height via lazy-load placeholders) before we read scrollHeight.
    useEffect(() => {
        if (!scrollerRef.current) return;
        const el = scrollerRef.current;
        const scrollToBottom = () => {
            el.scrollTop = el.scrollHeight;
        };
        // Two RAFs: first commits the layout, second runs after paint so
        // skeletons/images have their box reserved. Without this the chat
        // sometimes lands a few hundred px above the bottom on F5.
        requestAnimationFrame(() => {
            scrollToBottom();
            requestAnimationFrame(scrollToBottom);
        });
        // Also re-pin to bottom once images inside the message list finish
        // loading (they may grow the scroll height). One-shot listener that
        // unbinds itself once the chat has settled.
        const onLoad = (e) => {
            if (e.target.tagName === "IMG") scrollToBottom();
        };
        el.addEventListener("load", onLoad, true);
        const t = setTimeout(() => el.removeEventListener("load", onLoad, true), 4000);
        return () => {
            clearTimeout(t);
            el.removeEventListener("load", onLoad, true);
        };
    }, [messages.length, sending]);

    const currentModel = models.find((m) => m.id === settings.model_id);
    const supportsRef = currentModel?.supports_image_input;

    /**
     * Zone-edit state. When a user clicks the pencil on a generated image,
     * we stash {messageId, img} here so <ZoneEditor> can mount full-screen.
     * The resulting edited image REPLACES the slot in the originating
     * message.images list (match by library_id).
     */
    const [zoneEdit, setZoneEdit] = useState(null);
    /** Modal target for the "Redimensionar" feature. Null when closed. */
    const [resizeTarget, setResizeTarget] = useState(null);

    const replaceImageInMessage = (messageId, oldLibraryId, newInfo) => {
        setMessages((prev) =>
            prev.map((m) => {
                if (m.id !== messageId || !m.images) return m;
                return {
                    ...m,
                    images: m.images.map((img) =>
                        img.library_id === oldLibraryId
                            ? { ...img, ...newInfo }
                            : img,
                    ),
                };
            }),
        );
    };

    const togglePattern = (pid) => {
        setSettings((s) => ({
            ...s,
            patterns: s.patterns.includes(pid)
                ? s.patterns.filter((p) => p !== pid)
                : [...s.patterns, pid],
        }));
    };

    const setResolution = (res) => {
        setSettings((s) => ({ ...s, width: res.width, height: res.height }));
    };

    const addReferences = (refs) => {
        const newIds = refs.map((r) => r.file_id);
        setSettings((s) => {
            let nextModel = s.model_id;
            const curr = models.find((m) => m.id === s.model_id);
            if (curr && !curr.supports_image_input) {
                const i2i = models.find((m) => m.supports_image_input);
                if (i2i) nextModel = i2i.id;
            }
            return {
                ...s,
                model_id: nextModel,
                reference_image_ids: Array.from(
                    new Set([...s.reference_image_ids, ...newIds]),
                ),
            };
        });
        setRefThumbs((t) => {
            const existing = new Set(t.map((x) => x.file_id));
            return [...t, ...refs.filter((r) => !existing.has(r.file_id))];
        });
        setPickerOpen(false);
    };

    const removeReference = (fileId) => {
        setSettings((s) => ({
            ...s,
            reference_image_ids: s.reference_image_ids.filter((i) => i !== fileId),
        }));
        setRefThumbs((t) => t.filter((x) => x.file_id !== fileId));
    };

    // ── @mention autocomplete ──────────────────────────────────────────
    // Detect a mention token ending at the cursor: `@word|` where `|` is
    // the caret. Returns null when there's no active mention.
    const detectMention = (value, caret) => {
        const upto = value.slice(0, caret);
        const m = upto.match(/(?:^|\s)@([a-z0-9_-]*)$/i);
        if (!m) return null;
        return {
            query: m[1].toLowerCase(),
            tokenStart: caret - m[1].length - 1, // index of `@`
        };
    };

    // Debounced fetch of slug suggestions.
    useEffect(() => {
        if (!mention.active) return;
        let cancelled = false;
        setMention((s) => ({ ...s, loading: true }));
        const handle = setTimeout(async () => {
            try {
                const r = await api.get(
                    `/references/slugs/search?q=${encodeURIComponent(mention.query)}&limit=8`,
                );
                if (cancelled) return;
                setMention((s) => ({
                    ...s,
                    items: r.data?.items || [],
                    activeIdx: 0,
                    loading: false,
                }));
            } catch {
                if (cancelled) return;
                setMention((s) => ({ ...s, items: [], loading: false }));
            }
        }, 120);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [mention.active, mention.query]);

    // Insert the picked slug into the textarea AND attach the ref image.
    const acceptMention = (item) => {
        if (!item) return;
        setText((prev) => {
            const start = mention.tokenStart;
            if (start < 0) return prev;
            // Replace `@partial` with `@slug ` (trailing space so the
            // user can keep typing naturally).
            const before = prev.slice(0, start);
            const afterCaret = prev.slice(start).replace(/^@[a-z0-9_-]*/i, "");
            const inserted = `@${item.slug} `;
            const next = before + inserted + afterCaret;
            // Restore caret right after the inserted slug.
            const nextCaret = (before + inserted).length;
            setTimeout(() => {
                const el = textareaRef.current;
                if (el) {
                    el.focus();
                    el.setSelectionRange(nextCaret, nextCaret);
                }
            }, 0);
            return next;
        });
        // Attach as reference so the generator receives the image.
        addReferences([
            {
                file_id: item.file_id,
                storage_path: item.storage_path,
                title: item.title || item.slug,
                slug: item.slug,
            },
        ]);
        setMention({
            active: false,
            query: "",
            tokenStart: -1,
            items: [],
            activeIdx: 0,
            loading: false,
        });
        toast.success(`@${item.slug} adjuntado`, { duration: 1500 });
    };

    // Called by the textarea onChange right after setText.
    const onTextareaChange = (e) => {
        const value = e.target.value;
        setText(value);
        e.target.style.height = "auto";
        e.target.style.height = Math.min(e.target.scrollHeight, 220) + "px";
        const caret = e.target.selectionStart ?? value.length;
        const hit = detectMention(value, caret);
        if (hit) {
            setMention((s) => ({
                ...s,
                active: true,
                query: hit.query,
                tokenStart: hit.tokenStart,
            }));
        } else if (mention.active) {
            setMention((s) => ({ ...s, active: false, items: [] }));
        }
    };

    // Keyboard handling: navigate the mention popover OR send the message.
    const onTextareaKeyDown = (e) => {
        if (mention.active && mention.items.length > 0) {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setMention((s) => ({
                    ...s,
                    activeIdx: (s.activeIdx + 1) % s.items.length,
                }));
                return;
            }
            if (e.key === "ArrowUp") {
                e.preventDefault();
                setMention((s) => ({
                    ...s,
                    activeIdx:
                        (s.activeIdx - 1 + s.items.length) % s.items.length,
                }));
                return;
            }
            if (e.key === "Enter" || e.key === "Tab") {
                e.preventDefault();
                acceptMention(mention.items[mention.activeIdx]);
                return;
            }
            if (e.key === "Escape") {
                e.preventDefault();
                setMention((s) => ({ ...s, active: false }));
                return;
            }
        }
        // Default: Shift+Enter newline, Enter alone sends.
        if (
            e.key === "Enter" &&
            !e.shiftKey &&
            !e.nativeEvent.isComposing
        ) {
            e.preventDefault();
            if (text.trim() && !sending && !uploadingApoyo) send();
        }
    };

    /**
     * Upload a fresh image straight from the user's disk as an "apoyo".
     * Apoyos never come from the `/references` portfolio — they're ad-hoc
     * style cues attached to THIS chat message only. Each uploaded file is
     * stored via `/api/files/upload` and its `file_id` is appended to the
     * apoyoThumbs state (also mirrored into settings.reference_image_ids
     * so the backend receives them alongside the base references).
     */
    const uploadApoyos = async (files) => {
        const list = Array.from(files || []).filter((f) =>
            f.type.startsWith("image/"),
        );
        if (list.length === 0) return;
        setUploadingApoyo(true);
        setApoyoProgress(0);
        // Track per-file fractional progress so the aggregate (mean of all
        // files) reflects the user's mental model of "all attachments
        // ready" rather than just the current one.
        const fractions = new Array(list.length).fill(0);
        const recompute = () => {
            const total = fractions.reduce((a, b) => a + b, 0);
            setApoyoProgress(Math.round((total / fractions.length) * 100));
        };
        // Promote to an image-to-image model if the current one doesn't
        // accept image inputs, same guard we use for references.
        setSettings((s) => {
            const curr = models.find((m) => m.id === s.model_id);
            if (curr && !curr.supports_image_input) {
                const i2i = models.find((m) => m.supports_image_input);
                if (i2i) return { ...s, model_id: i2i.id };
            }
            return s;
        });
        try {
            for (let i = 0; i < list.length; i++) {
                const f = list[i];
                const fd = new FormData();
                fd.append("file", f);
                const r = await api.post("/files/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                    onUploadProgress: (ev) => {
                        if (ev.total) {
                            fractions[i] = Math.min(1, ev.loaded / ev.total);
                            recompute();
                        }
                    },
                });
                fractions[i] = 1;
                recompute();
                const doc = r.data;
                setApoyoThumbs((t) => [
                    ...t,
                    {
                        file_id: doc.id,
                        storage_path: doc.storage_path,
                        title: doc.original_filename || "apoyo",
                    },
                ]);
                setSettings((s) => ({
                    ...s,
                    reference_image_ids: [...s.reference_image_ids, doc.id],
                }));
            }
            toast.success(
                `${list.length} apoyo${list.length === 1 ? "" : "s"} subido${
                    list.length === 1 ? "" : "s"
                }`,
            );
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo subir el apoyo",
            );
        } finally {
            setUploadingApoyo(false);
            setApoyoProgress(0);
        }
    };

    const onApoyoFileChange = (e) => {
        const files = e.target.files;
        uploadApoyos(files);
        // Reset so re-picking the same file still triggers `change`.
        if (e.target) e.target.value = "";
    };

    /* --- Drag-and-drop apoyo uploads ---
     * A user can drag image files (or even reference cards from
     * /references — those carry a `text/plain` payload but we ignore
     * them here; only real File objects from the OS produce apoyo
     * uploads) onto anywhere inside <main>. The overlay shows a
     * dashed border + hint. We intentionally only handle real File
     * drops because dragging a remote `<img>` doesn't produce a File.
     */
    const onChatDragEnter = (e) => {
        if (!e.dataTransfer?.types?.includes("Files")) return;
        e.preventDefault();
        setDragDepth((d) => d + 1);
    };
    const onChatDragOver = (e) => {
        if (!e.dataTransfer?.types?.includes("Files")) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
    };
    const onChatDragLeave = (e) => {
        if (!e.dataTransfer?.types?.includes("Files")) return;
        e.preventDefault();
        setDragDepth((d) => Math.max(0, d - 1));
    };
    const onChatDrop = (e) => {
        if (!e.dataTransfer?.types?.includes("Files")) return;
        e.preventDefault();
        setDragDepth(0);
        const files = Array.from(e.dataTransfer.files || []).filter((f) =>
            (f.type || "").startsWith("image/"),
        );
        if (files.length === 0) {
            toast.error("Suelta una imagen para usarla como apoyo");
            return;
        }
        uploadApoyos(files);
    };

    const removeApoyo = (fileId) => {
        setSettings((s) => ({
            ...s,
            reference_image_ids: s.reference_image_ids.filter((i) => i !== fileId),
        }));
        setApoyoThumbs((t) => t.filter((x) => x.file_id !== fileId));
    };

    const send = async () => {
        if (!text.trim() || sending || !settings.model_id) return;
        // Safety guard: even if the user manages to keystroke-trigger
        // send while files are still uploading, refuse — the backend
        // would otherwise generate without the references the user
        // expects to be applied.
        if (uploadingApoyo) {
            toast.warning("Espera a que terminen de cargar los adjuntos");
            return;
        }
        setSending(true);
        // Auto-collapse the refs strip the moment the user fires Generar so
        // the freshly generated image gets maximum vertical real-estate.
        // The user can manually re-open it via the toggle if they want to
        // tweak references for the next iteration.
        setRefsCollapsed(true);
        const currentText = text;

        // Resolve any `@slug` mentions still in the prompt that aren't
        // already attached as refs. Users can type them manually (or the
        // picker was closed by mistake) — we still want the generator to
        // receive the image. This is a best-effort resolve: unknown slugs
        // are silently ignored so the prompt is never blocked.
        const mentionMatches = Array.from(
            currentText.matchAll(/(?:^|\s)@([a-z0-9][a-z0-9_-]{1,39})\b/gi),
        ).map((m) => m[1].toLowerCase());
        const attachedSlugs = new Set(
            refThumbs.map((r) => r.slug).filter(Boolean),
        );
        const missingSlugs = Array.from(
            new Set(mentionMatches.filter((s) => !attachedSlugs.has(s))),
        );
        let extraMentionRefs = [];
        if (missingSlugs.length > 0) {
            try {
                const r = await api.post("/references/slugs/resolve", {
                    slugs: missingSlugs,
                });
                extraMentionRefs = (r.data?.items || []).map((it) => ({
                    file_id: it.file_id,
                    storage_path: it.storage_path,
                    title: it.title || it.slug,
                    slug: it.slug,
                }));
                if (extraMentionRefs.length > 0) {
                    setRefThumbs((t) => {
                        const existing = new Set(t.map((x) => x.file_id));
                        return [
                            ...t,
                            ...extraMentionRefs.filter(
                                (x) => !existing.has(x.file_id),
                            ),
                        ];
                    });
                }
            } catch {
                // Non-blocking warning: mentions couldn't be resolved. We
                // still let the message go through — the model will just
                // see the raw `@slug` text without the image.
                toast.warning(
                    "No se pudieron adjuntar todas las menciones. Revisa los nombres.",
                    { duration: 4000 },
                );
            }
        }

        // Resolve chat id — create the chat lazily on the first message send
        // so we never produce empty chat records.
        let chatId = id;
        try {
            if (!chatId) {
                const r = await api.post("/chats", {
                    title: currentText.slice(0, 60) || "Nuevo chat",
                });
                chatId = r.data.id;
                setChat(r.data);
                // Update the URL so refreshes / deep-links work. We use
                // `window.history.replaceState` here (NOT `navigate`) because
                // the Chat component is keyed by the URL id in App.js —
                // calling `navigate` would remount Chat mid-send and wipe
                // the in-flight state (messages, sending flag, etc.). The
                // "Nuevo chat" remount bug is fixed separately by making the
                // new-chat button explicitly force a remount instead of
                // relying on react-router params to have updated.
                window.history.replaceState(null, "", `/chats/${chatId}`);
                window.dispatchEvent(new CustomEvent("kc:chats-updated"));
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo crear el chat",
            );
            setSending(false);
            return;
        }

        const userStub = {
            id: "tmp-" + Date.now(),
            role: "user",
            text: currentText,
            settings,
            reference_image_ids: settings.reference_image_ids,
            created_at: new Date().toISOString(),
        };
        setMessages((m) => [...m, userStub]);
        setText("");
        try {
            // Split reference_image_ids into two groups before sending so
            // the assistant message can render them distinctly (teal for
            // portfolio references, amber for chat uploads).
            const referenceIds = Array.from(
                new Set([
                    ...refThumbs.map((r) => r.file_id),
                    ...extraMentionRefs.map((r) => r.file_id),
                ]),
            );
            const apoyoIds = apoyoThumbs.map((a) => a.file_id);
            const r = await api.post(`/chats/${chatId}/messages`, {
                text: currentText,
                settings: {
                    ...settings,
                    reference_image_ids: [...referenceIds, ...apoyoIds],
                    apoyo_image_ids: apoyoIds,
                },
            });
            setMessages((m) => [
                ...m.filter((x) => x.id !== userStub.id),
                r.data.user_message,
                r.data.assistant_message,
            ]);
            // Register the assistant placeholder with the global tracker
            // RIGHT AWAY (don't wait for the messages-effect downstream)
            // so a user who clicks "Nuevo chat" + types in chat B before
            // the local state settles still has the chat A generation
            // tracked. trackGeneration is idempotent.
            if (
                r.data?.assistant_message?.id &&
                r.data?.assistant_message?.status === "generating"
            ) {
                trackGeneration(chatId, r.data.assistant_message.id);
            }
            window.dispatchEvent(new CustomEvent("kc:credits-updated"));
            window.dispatchEvent(new CustomEvent("kc:chats-updated"));
        } catch (e) {
            // 504 / 502 / network errors can still happen if the request
            // takes >60s to reach the server even before the bg-task kicks
            // in (rare, but possible under heavy ingress load). In those
            // cases the bg work likely STILL ran on the backend — the
            // placeholder message is already in Mongo. Recover gracefully:
            // refetch the message list so the in-flight placeholder shows
            // up and the polling loop takes it to completion. No scary
            // error toast, because the work isn't actually failing.
            const status = e?.response?.status;
            const isTimeout = status === 504 || status === 502 || !status;
            if (isTimeout) {
                try {
                    const fresh = await api.get(`/chats/${chatId}/messages`);
                    setMessages(fresh.data);
                } catch {
                    /* if even the GET fails, fall through to the error toast */
                }
            } else {
                const detail =
                    e?.response?.data?.detail ||
                    "Error generando imagen. Intenta de nuevo.";
                toast.error(detail, { duration: 6000 });
                setMessages((m) => m.filter((x) => x.id !== userStub.id));
            }
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="flex h-screen bg-ink-950 overflow-hidden">
            <Sidebar activeChatId={id} />
            <main
                className="flex-1 flex flex-col min-w-0 min-h-0 relative"
                onDragEnter={onChatDragEnter}
                onDragOver={onChatDragOver}
                onDragLeave={onChatDragLeave}
                onDrop={onChatDrop}
                data-testid="chat-dropzone"
            >
                {/* Drag-and-drop overlay — only visible while the user
                  * is dragging files over the chat. Pointer-events stay
                  * disabled so the drop event still bubbles to <main>. */}
                {dragActive && (
                    <div
                        data-testid="chat-drop-overlay"
                        className="absolute inset-0 z-40 pointer-events-none flex items-center justify-center bg-ink-950/70 backdrop-blur-sm"
                    >
                        <div className="rounded-2xl border-2 border-dashed border-periwinkle-400/70 px-10 py-8 text-center">
                            <Paperclip className="w-8 h-8 text-periwinkle-800 mx-auto mb-2" />
                            <p className="text-fog-100 text-sm font-display">
                                Suelta para añadir como apoyo
                            </p>
                            <p className="text-fog-400 text-xs mt-1">
                                Las imágenes se cargan en este chat como
                                referencia de estilo
                            </p>
                        </div>
                    </div>
                )}
                {/* top header: chat title · help · consumption · drawer toggle */}
                <header className="h-16 shrink-0 flex items-center gap-3 px-6 border-b hairline border-ink-600 glass">
                    <div className="flex items-center gap-2 min-w-0">
                        <Sparkles className="w-4 h-4 text-periwinkle-800 shrink-0" />
                        <span className="font-display text-sm text-fog-300 truncate">
                            {chat?.title || "Chat Studio"}
                        </span>
                    </div>
                    <div className="flex-1" />
                    <button
                        data-testid="chat-help-btn"
                        onClick={() => setHelpOpen(true)}
                        className="rounded-full border hairline border-ink-600 text-fog-400 hover:text-periwinkle-800 hover:border-periwinkle-500 transition-colors w-9 h-9 flex items-center justify-center shrink-0"
                        aria-label="¿Cómo funciona?"
                        title="¿Cómo funciona? Referencia vs Apoyo"
                    >
                        <HelpCircle className="w-4 h-4" />
                    </button>
                    <ConsumptionBadges hideQuota />
                    <button
                        data-testid="chat-toggle-drawer-btn"
                        onClick={() => setDrawerOpen((v) => !v)}
                        className="ml-1 rounded-lg border hairline border-ink-600 text-fog-400 hover:text-periwinkle-800 hover:border-periwinkle-500 transition-colors w-9 h-9 flex items-center justify-center shrink-0"
                        aria-label={drawerOpen ? "Ocultar chats" : "Mostrar chats"}
                        title={drawerOpen ? "Ocultar historial" : "Mostrar historial"}
                    >
                        {drawerOpen ? (
                            <PanelRightClose className="w-4 h-4" />
                        ) : (
                            <PanelRightOpen className="w-4 h-4" />
                        )}
                    </button>
                </header>

                <div className="flex-1 flex min-w-0 min-h-0">
                    {/* chat column — fixed column with internal scroll for messages */}
                    <div className="flex-1 flex flex-col min-w-0 min-h-0">
                        <div
                            ref={scrollerRef}
                            className="flex-1 overflow-y-auto min-h-0 px-6 lg:px-10 py-8"
                        >
                            <div className="max-w-3xl mx-auto space-y-8">
                                {messages.length === 0 && !sending && (
                                    <EmptyState title={chat?.title || "Chat General"} />
                                )}
                                {messages.map((m, idx) => {
                                    // Pair the assistant message with the
                                    // previous user message to surface the
                                    // settings used (model, resolution,
                                    // creativity, patterns).
                                    const prevUser =
                                        m.role === "assistant"
                                            ? [...messages.slice(0, idx)]
                                                  .reverse()
                                                  .find((x) => x.role === "user")
                                            : null;
                                    // Only the LAST assistant message is
                                    // treated as "fresh" → its images load
                                    // eagerly. Everything older relies on
                                    // viewport-based lazy loading so the
                                    // chat doesn't fire dozens of GETs at
                                    // once, which is what was making big
                                    // chat histories crawl.
                                    const isLatestAssistant =
                                        m.role === "assistant" &&
                                        idx === messages.length - 1;
                                    return (
                                        <MessageBubble
                                            key={m.id}
                                            m={m}
                                            userSettings={
                                                prevUser?.settings || null
                                            }
                                            userText={prevUser?.text || ""}
                                            modelsCatalog={Array.isArray(models) ? models : []}
                                            patternsCatalog={flatPatterns}
                                            chatId={id}
                                            chatTitle={chat?.title || ""}
                                            myRatings={myRatings}
                                            isLatestAssistant={isLatestAssistant}
                                            onEditZone={(img) =>
                                                setZoneEdit({
                                                    messageId: m.id,
                                                    img,
                                                })
                                            }
                                            onResize={(img) =>
                                                setResizeTarget({
                                                    messageId: m.id,
                                                    img,
                                                })
                                            }
                                            onRetry={(retryText, retrySettings) => {
                                                setText(retryText);
                                                setSettings((s) => ({
                                                    ...s,
                                                    ...retrySettings,
                                                }));
                                                toast.info(
                                                    "Prompt y ajustes cargados — pulsa Generar",
                                                );
                                            }}
                                        />
                                    );
                                })}
                                {sending && <GeneratingPlaceholder />}
                            </div>
                        </div>

                        {/* composer — always at bottom, never scrolls with messages */}
                        <div className="shrink-0 border-t hairline border-ink-600 glass">
                            <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-3 pb-4 space-y-3">
                                {/* References (base) + Apoyos (uploads) strip when active */}
                                {(refThumbs.length > 0 || apoyoThumbs.length > 0) && (
                                    <RefsStrip
                                        refs={refThumbs}
                                        apoyos={apoyoThumbs}
                                        supportsRef={supportsRef}
                                        currentModelName={currentModel?.name}
                                        onRemoveRef={removeReference}
                                        onRemoveApoyo={removeApoyo}
                                        collapsed={refsCollapsed}
                                        onToggleCollapsed={() =>
                                            setRefsCollapsed((v) => !v)
                                        }
                                    />
                                )}

                                {/* Configuration popover — opens above the
                                  * composer. Includes EVERYTHING the user can
                                  * tweak: model, quality, resolution, carousel
                                  * count, creativity, references picker, and
                                  * (when admin allows) construction patterns.
                                  * Wrapped in a backdrop so clicking outside
                                  * closes it cleanly. */}
                                {configOpen && (
                                    <>
                                        <div
                                            data-testid="chat-config-backdrop"
                                            onClick={() => setConfigOpen(false)}
                                            className="fixed inset-0 bg-ink-950/60 z-40 animate-fade-in"
                                        />
                                        <div
                                            data-testid="chat-config-panel"
                                            className="fixed inset-x-0 bottom-0 z-50 max-w-3xl mx-auto px-6 lg:px-10 pb-4 pointer-events-none"
                                        >
                                            <div className="pointer-events-auto rounded-2xl border hairline border-periwinkle-500/30 bg-ink-900/95 backdrop-blur-xl shadow-2xl p-5 space-y-4 max-h-[78vh] overflow-y-auto animate-fade-up">
                                                <div className="flex items-center justify-between gap-2">
                                                    <p className="font-mono text-[10px] tracking-[0.3em] text-periwinkle-800 uppercase flex items-center gap-2">
                                                        <Settings className="w-3 h-3" />
                                                        Configuración del chat
                                                    </p>
                                                    <button
                                                        data-testid="chat-config-close-btn"
                                                        onClick={() => setConfigOpen(false)}
                                                        className="w-8 h-8 rounded-full hover:bg-ink-700/60 text-fog-400 hover:text-fog-100 transition-colors flex items-center justify-center"
                                                        title="Cerrar"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                                <div className="grid md:grid-cols-2 gap-3">
                                                    <div className="md:col-span-2">
                                                        <p className="font-mono text-[10px] tracking-[0.25em] text-fog-400 uppercase mb-2">
                                                            Modelo de IA
                                                        </p>
                                                        <ModelDropdown
                                                            models={models}
                                                            value={settings.model_id}
                                                            onChange={(v) =>
                                                                setSettings((s) => ({ ...s, model_id: v }))
                                                            }
                                                            currency={currency}
                                                            trm={trm}
                                                        />
                                                    </div>
                                                    <div className="md:col-span-2">
                                                        <p className="font-mono text-[10px] tracking-[0.25em] text-fog-400 uppercase mb-2">
                                                            Calidad de la imagen
                                                        </p>
                                                        <QualityPicker
                                                            value={settings.quality}
                                                            onChange={(q) =>
                                                                setSettings((s) => ({ ...s, quality: q }))
                                                            }
                                                            surchargeUsd={qualitySurchargeUsd}
                                                            currency={currency}
                                                            trm={trm}
                                                        />
                                                    </div>
                                                    <ResolutionPicker
                                                        resolutions={resolutions}
                                                        width={settings.width}
                                                        height={settings.height}
                                                        onChange={setResolution}
                                                    />
                                                    <CarouselPicker
                                                        value={settings.num_images}
                                                        onChange={(v) =>
                                                            setSettings((s) => ({ ...s, num_images: v }))
                                                        }
                                                    />
                                                    <div>
                                                        <p className="font-mono text-[10px] tracking-[0.25em] text-fog-400 uppercase mb-2">
                                                            Nivel de creatividad
                                                        </p>
                                                        <div className="relative rounded-xl border hairline border-ink-600 bg-ink-900 hover:border-periwinkle-500/60 focus-within:border-periwinkle-500 transition-colors">
                                                            <select
                                                                data-testid="chat-config-creativity"
                                                                value={settings.creativity_level}
                                                                onChange={(e) =>
                                                                    setSettings((s) => ({
                                                                        ...s,
                                                                        creativity_level: e.target.value,
                                                                    }))
                                                                }
                                                                className="w-full appearance-none bg-transparent pl-4 pr-9 py-3 text-sm text-fog-100 outline-none cursor-pointer"
                                                            >
                                                                {CREATIVITY_OPTIONS.map((o) => (
                                                                    <option key={o.value} value={o.value}>
                                                                        {o.label}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fog-400" />
                                                        </div>
                                                        <p className="font-mono text-[9px] text-fog-500 mt-1.5 leading-tight">
                                                            {CREATIVITY_OPTIONS.find(
                                                                (o) => o.value === settings.creativity_level,
                                                            )?.desc}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p className="font-mono text-[10px] tracking-[0.25em] text-fog-400 uppercase mb-2">
                                                            Referencias (punto base)
                                                        </p>
                                                        <button
                                                            data-testid="chat-config-add-ref"
                                                            onClick={() => setPickerOpen(true)}
                                                            className="w-full rounded-xl border hairline border-periwinkle-500/40 text-periwinkle-800 hover:bg-periwinkle-500/5 hover:border-periwinkle-500 px-4 py-3 text-sm flex items-center justify-center gap-2"
                                                        >
                                                            <ImageIcon className="w-4 h-4" />
                                                            {refThumbs.length > 0
                                                                ? `${refThumbs.length} referencia${refThumbs.length === 1 ? "" : "s"} · añadir más`
                                                                : "Traer desde /referencias"}
                                                        </button>
                                                        <p className="font-mono text-[9px] text-fog-500 mt-1.5 leading-tight">
                                                            Portafolio de la marca, externas, biblioteca o logos.
                                                        </p>
                                                    </div>
                                                    {patterns && showPatternsInChat && (
                                                        <div className="md:col-span-2">
                                                            <PatternSelector
                                                                patterns={patterns}
                                                                selected={settings.patterns}
                                                                onToggle={togglePattern}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                {/* Pinned summary inside the panel so the user
                                                  * always sees the final resolution + cost while
                                                  * tweaking the settings. */}
                                                <QualitySummary
                                                    quality={settings.quality}
                                                    width={settings.width}
                                                    height={settings.height}
                                                    model={models.find((m) => m.id === settings.model_id)}
                                                    surchargeUsd={qualitySurchargeUsd}
                                                    currency={currency}
                                                    trm={trm}
                                                    refCount={refThumbs.length}
                                                    apoyoCount={apoyoThumbs.length}
                                                    extraRefCostUsd={extraRefCostUsd}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Compact summary chip — clicking opens the
                                  * config popover. Replaces the always-visible
                                  * toolbar so the chat history gets maximum
                                  * vertical real-estate. */}
                                <ConfigSummaryChip
                                    settings={settings}
                                    models={models}
                                    surchargeUsd={qualitySurchargeUsd}
                                    currency={currency}
                                    trm={trm}
                                    refCount={refThumbs.length}
                                    apoyoCount={apoyoThumbs.length}
                                    extraRefCostUsd={extraRefCostUsd}
                                    onClick={() => setConfigOpen(true)}
                                />

                                {/* Input */}
                                <div className="flex items-end gap-2 relative">
                                    <MentionAutocomplete
                                        active={mention.active}
                                        query={mention.query}
                                        loading={mention.loading}
                                        items={mention.items}
                                        activeIdx={mention.activeIdx}
                                        onSelect={acceptMention}
                                    />
                                    <input
                                        ref={apoyoInputRef}
                                        data-testid="chat-apoyo-upload-input"
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={onApoyoFileChange}
                                        className="hidden"
                                    />
                                    <button
                                        data-testid="chat-add-reference-btn"
                                        onClick={() => apoyoInputRef.current?.click()}
                                        disabled={uploadingApoyo}
                                        title="Subir apoyo (imagen desde tu equipo)"
                                        className="w-11 h-11 shrink-0 rounded-xl border hairline border-amber-400/40 text-amber-300 hover:text-amber-200 hover:border-amber-400 transition-colors flex items-center justify-center disabled:opacity-50"
                                    >
                                        <Paperclip className="w-4 h-4" />
                                    </button>
                                    <button
                                        data-testid="chat-open-config-btn"
                                        onClick={() => setConfigOpen(true)}
                                        title="Configuración del chat (modelo, calidad, resolución, patrones)"
                                        className={`w-11 h-11 shrink-0 rounded-xl border hairline transition-colors flex items-center justify-center ${
                                            configOpen
                                                ? "border-periwinkle-500 text-periwinkle-800 bg-periwinkle-500/10"
                                                : "border-ink-600 text-fog-400 hover:text-fog-100"
                                        }`}
                                    >
                                        <Settings className="w-4 h-4" />
                                    </button>
                                    <button
                                        type="button"
                                        data-testid="chat-resize-btn"
                                        onClick={() => setResizePickerOpen(true)}
                                        title="Redimensionar una imagen — escoge una pieza base y genera variantes en otros formatos"
                                        className="w-11 h-11 shrink-0 rounded-xl border hairline border-periwinkle-500/40 text-periwinkle-800 hover:text-periwinkle-800 hover:border-periwinkle-500 transition-colors flex items-center justify-center"
                                    >
                                        <Maximize2 className="w-4 h-4" />
                                    </button>
                                    <textarea
                                        ref={textareaRef}
                                        data-testid="chat-prompt-input"
                                        rows={2}
                                        value={text}
                                        onChange={onTextareaChange}
                                        onKeyDown={onTextareaKeyDown}
                                        onBlur={() =>
                                            // Close the popover on blur — mousedown on
                                            // an option runs first (preventDefault above),
                                            // so real clicks still register.
                                            setTimeout(
                                                () =>
                                                    setMention((s) =>
                                                        s.active
                                                            ? { ...s, active: false }
                                                            : s,
                                                    ),
                                                150,
                                            )
                                        }
                                        placeholder="¿Qué quieres generar hoy? Usa @nombre para incluir una referencia. (Shift+Enter para nueva línea)"
                                        className="flex-1 rounded-xl bg-ink-900/60 border hairline border-ink-600 focus:border-periwinkle-500 text-fog-100 px-4 py-3 outline-none resize-none placeholder:text-fog-500 leading-relaxed max-h-[220px] min-h-[52px]"
                                    />
                                    <button
                                        data-testid="chat-send-btn"
                                        onClick={send}
                                        disabled={!text.trim() || sending || uploadingApoyo}
                                        title={
                                            uploadingApoyo
                                                ? `Esperando adjuntos: ${apoyoProgress}%`
                                                : "Enviar prompt y generar"
                                        }
                                        className="relative h-11 px-5 rounded-xl text-ink-on font-medium flex items-center gap-2 transition-all disabled:cursor-not-allowed bg-ink-700 overflow-hidden"
                                    >
                                        {/* Animated periwinkle fill that grows from 0→100% while
                                          * the apoyo files are uploading. Once idle and ready,
                                          * it stays at 100% so the button looks fully active. */}
                                        <span
                                            aria-hidden="true"
                                            className="absolute inset-y-0 left-0 bg-periwinkle-500 transition-[width] duration-300 ease-out"
                                            style={{
                                                width: uploadingApoyo
                                                    ? `${apoyoProgress}%`
                                                    : text.trim() && !sending
                                                      ? "100%"
                                                      : "0%",
                                            }}
                                        />
                                        <Send className="w-4 h-4 relative z-10" />
                                        <span className="relative z-10">
                                            {uploadingApoyo
                                                ? `Cargando ${apoyoProgress}%`
                                                : "Generar"}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <ChatsDrawer
                        activeChatId={id}
                        open={drawerOpen}
                        onClose={() => setDrawerOpen(false)}
                    />
                </div>
            </main>

            {pickerOpen && (
                <ReferencePicker
                    onClose={() => setPickerOpen(false)}
                    onPick={addReferences}
                />
            )}

            {resizePickerOpen && (
                <ReferencePicker
                    onClose={() => setResizePickerOpen(false)}
                    onPick={(picked) => {
                        setResizePickerOpen(false);
                        if (!picked || picked.length === 0) return;
                        // Standalone resize handles one source at a time. If the
                        // user picked multiple, take the first and surface a hint
                        // so they understand only one was used.
                        if (picked.length > 1) {
                            toast.info(
                                `Se usará "${picked[0].title || "la primera"}" como base — redimensiona una imagen a la vez.`,
                            );
                        }
                        setStandaloneResize(picked[0]);
                    }}
                />
            )}

            {zoneEdit && (
                <ZoneEditor
                    chatId={id || chat?.id}
                    messageId={zoneEdit.messageId}
                    img={zoneEdit.img}
                    onClose={() => setZoneEdit(null)}
                    onReplaced={(newInfo) =>
                        replaceImageInMessage(
                            zoneEdit.messageId,
                            zoneEdit.img.library_id,
                            newInfo,
                        )
                    }
                />
            )}

            {resizeTarget && (
                <ResizeDialog
                    target={{
                        chatId: id || chat?.id,
                        messageId: resizeTarget.messageId,
                        img: resizeTarget.img,
                    }}
                    onClose={() => setResizeTarget(null)}
                    onResolved={(newMessage) => {
                        // Append the new "resize" message to the conversation.
                        // Keep the dialog open so the user can pick another
                        // preset for the same source without re-clicking the
                        // resize button.
                        setMessages((prev) => [...prev, newMessage]);
                    }}
                />
            )}

            {standaloneResize && (
                <ResizeDialog
                    source={standaloneResize}
                    chatId={id || chat?.id || null}
                    onClose={() => setStandaloneResize(null)}
                    onResolved={({ userMessage, message, chatId: returnedChatId, createdNewChat }) => {
                        // If the backend opened a brand-new chat (because we
                        // didn't have one yet), navigate to it. The chat will
                        // load with both messages already persisted.
                        if (createdNewChat && returnedChatId) {
                            navigate(`/chats/${returnedChatId}`);
                            return;
                        }
                        // Otherwise we're already in the right chat — append
                        // the user-prompt + assistant pair so it appears live
                        // without a refetch.
                        setMessages((prev) => [
                            ...prev,
                            ...(userMessage ? [userMessage] : []),
                            message,
                        ]);
                    }}
                />
            )}

            <HowItWorksModal
                open={helpOpen}
                onClose={() => setHelpOpen(false)}
            />
        </div>
    );
}

function MiniChip({ label, tone = "neutral" }) {
    const t =
        tone === "peri"
            ? "border-periwinkle-500/50 text-periwinkle-800 bg-periwinkle-500/5"
            : "border-ink-600 text-fog-400 bg-ink-900/40";
    return (
        <span
            className={`rounded-full border hairline px-2.5 py-1 font-mono text-[10px] tracking-widest uppercase ${t}`}
        >
            {label}
        </span>
    );
}

function RefsStrip({
    refs,
    apoyos,
    supportsRef,
    currentModelName,
    onRemoveRef,
    onRemoveApoyo,
    collapsed,
    onToggleCollapsed,
}) {
    const hasRef = refs.length > 0;
    const hasApoyo = apoyos.length > 0;
    return (
        <div className="space-y-3" data-testid="refs-and-apoyos-strip">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-periwinkle-500/15 border hairline border-periwinkle-500/40 px-2.5 py-1 font-mono text-[9px] tracking-widest uppercase text-periwinkle-800">
                    <span className="w-1 h-1 rounded-full bg-periwinkle-500 animate-pulse-soft" />
                    Modo edición
                </span>
                <span className="font-mono text-[10px] text-fog-400">
                    {hasRef
                        ? `${refs.length} referencia${refs.length === 1 ? "" : "s"} base`
                        : "sin referencia base"}
                    {hasApoyo
                        ? ` · ${apoyos.length} apoyo${apoyos.length === 1 ? "" : "s"}`
                        : ""}
                </span>
                <div className="flex-1" />
                <button
                    data-testid="refs-strip-toggle"
                    onClick={() => onToggleCollapsed?.()}
                    className="inline-flex items-center gap-1.5 rounded-full border hairline border-ink-600 text-fog-400 hover:text-fog-100 hover:border-periwinkle-500 transition-colors px-2.5 py-1 font-mono text-[9px] tracking-widest uppercase"
                    title={collapsed ? "Mostrar adjuntos" : "Ocultar adjuntos"}
                    aria-expanded={!collapsed}
                    aria-label={collapsed ? "Mostrar adjuntos" : "Ocultar adjuntos"}
                >
                    {collapsed ? (
                        <>
                            <ChevronDown className="w-3 h-3" />
                            Mostrar
                        </>
                    ) : (
                        <>
                            <ChevronUp className="w-3 h-3" />
                            Ocultar
                        </>
                    )}
                </button>
            </div>
            {!supportsRef && !collapsed && (
                <p className="rounded-lg bg-amber-400/10 border hairline border-amber-400/30 px-3 py-2 text-[11px] text-amber-200">
                    ⚠ El modelo seleccionado ({currentModelName}) no acepta imágenes.
                    Cambia a un modelo <strong>image-to-image</strong> (Kontext, Nano
                    Banana · Edit) o los archivos adjuntos se ignorarán.
                </p>
            )}

            {!collapsed && hasRef && (
                <ThumbRow
                    label="Referencias · punto base"
                    accent="peri"
                    items={refs}
                    onRemove={onRemoveRef}
                    testIdPrefix="remove-ref"
                    tagText="Referencia"
                />
            )}
            {!collapsed && hasApoyo && (
                <ThumbRow
                    label="Apoyos · uploads del chat"
                    accent="amber"
                    items={apoyos}
                    onRemove={onRemoveApoyo}
                    testIdPrefix="remove-apoyo"
                    tagText="Apoyo"
                />
            )}
        </div>
    );
}

function ThumbRow({ label, accent, items, onRemove, testIdPrefix, tagText }) {
    const borderCls =
        accent === "peri"
            ? "border-periwinkle-500"
            : "border-amber-400/60";
    const tagBg =
        accent === "peri"
            ? "bg-periwinkle-500/90 text-periwinkle-ink"
            : "bg-amber-400/85 text-ink-on";
    const labelCls =
        accent === "peri"
            ? "text-periwinkle-800"
            : "text-amber-300";
    return (
        <div className="space-y-1.5">
            <p
                className={`font-mono text-[9px] tracking-widest uppercase ${labelCls}`}
            >
                {label}
            </p>
            <div className="flex gap-2 flex-wrap">
                {items.map((r) => (
                    <div
                        key={r.file_id}
                        className={`relative w-16 h-16 rounded-lg overflow-hidden border hairline ${borderCls} group`}
                        title={r.title || tagText}
                    >
                        <RetryImage
                            src={thumbUrl(r.storage_path, 160)}
                            alt={r.title || tagText}
                            className="w-full h-full object-cover"
                        />
                        <span
                            className={`absolute bottom-0 left-0 right-0 font-mono text-[8px] tracking-widest text-center uppercase py-0.5 ${tagBg}`}
                            aria-hidden="true"
                        >
                            {tagText}
                        </span>
                        <button
                            data-testid={`${testIdPrefix}-${r.file_id}`}
                            onClick={() => onRemove(r.file_id)}
                            className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-ink-950/80 text-fog-100 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

function EmptyState({ title }) {
    return (
        <div className="text-center py-24 animate-fade-up" data-testid="chat-empty-state">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-ink-800 border hairline border-ink-600 mb-6">
                <Sparkles className="w-7 h-7 text-periwinkle-800" />
            </div>
            <h2 className="font-display text-4xl text-fog-100 mb-3">{title}</h2>
            <p className="text-fog-400 max-w-md mx-auto text-sm leading-relaxed">
                Trae una <strong>referencia base</strong> desde Config, sube{" "}
                <strong>apoyos</strong> con el clip 📎 y escribe el cambio que
                quieres.
            </p>
            <p className="text-fog-500 text-xs mt-4 max-w-md mx-auto leading-relaxed">
                <span className="text-periwinkle-800">Tip:</span> este chat tiene{" "}
                <strong>memoria</strong> — cada nueva imagen se construye sobre la
                dirección anterior. Si quieres cambiar de referente, abre un{" "}
                <strong>nuevo chat</strong>.
            </p>
        </div>
    );
}

function GeneratingPlaceholder() {
    return (
        <div className="flex gap-4 animate-fade-up">
            <div className="w-8 h-8 rounded-full bg-periwinkle-500/20 border hairline border-periwinkle-500 flex items-center justify-center shrink-0">
                <Sparkles className="w-3.5 h-3.5 text-periwinkle-800 animate-pulse-soft" />
            </div>
            <div className="flex-1 space-y-3">
                <p className="font-mono text-[11px] text-fog-400 tracking-widest uppercase">
                    Refinando prompt · Generando imagen…
                </p>
                <div className="grid grid-cols-2 gap-3 max-w-xl">
                    {[0, 1].map((i) => (
                        <div
                            key={i}
                            className="aspect-square rounded-xl bg-ink-800 relative overflow-hidden"
                        >
                            <div
                                className="absolute inset-0 opacity-40 animate-shimmer"
                                style={{
                                    background:
                                        "linear-gradient(90deg, transparent, rgba(165,166,255,0.2), transparent)",
                                    backgroundSize: "200% 100%",
                                }}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function MessageBubble({ m, userSettings, userText, modelsCatalog, patternsCatalog, onEditZone, onResize, onRetry, chatId, chatTitle, myRatings, isLatestAssistant = false }) {
    const [refsMeta, setRefsMeta] = useState([]);
    const [apoyosMeta, setApoyosMeta] = useState([]);
    const [inspirationOpen, setInspirationOpen] = useState(false);

    useEffect(() => {
        if (m.role !== "assistant") {
            setRefsMeta([]);
            setApoyosMeta([]);
            return;
        }
        const refIds = m.reference_image_ids || [];
        const apoyoIds = m.apoyo_image_ids || [];
        // `reference_image_ids` on old messages may contain both kinds (we
        // used to collapse them). Filter out apoyo ids from the reference
        // list so the UI never shows a thumb twice.
        const pureRefIds = refIds.filter((id) => !apoyoIds.includes(id));
        const allIds = [...new Set([...pureRefIds, ...apoyoIds])];
        if (allIds.length === 0) {
            setRefsMeta([]);
            setApoyosMeta([]);
            return;
        }
        api.post("/files/resolve", { ids: allIds })
            .then((r) => {
                const map = new Map((r.data || []).map((d) => [d.id, d]));
                setRefsMeta(pureRefIds.map((id) => map.get(id)).filter(Boolean));
                setApoyosMeta(apoyoIds.map((id) => map.get(id)).filter(Boolean));
            })
            .catch(() => {
                setRefsMeta([]);
                setApoyosMeta([]);
            });
    }, [m.id, m.role, m.reference_image_ids, m.apoyo_image_ids]);

    if (m.role === "user") {
        return (
            <div className="flex justify-end animate-fade-up">
                <div className="max-w-[80%] rounded-2xl rounded-tr-sm bg-ink-800 border hairline border-ink-600 text-fog-100 px-4 py-3">
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">
                        {m.text}
                    </p>
                </div>
            </div>
        );
    }
    return (
        <div className="flex gap-4 animate-fade-up">
            <div className="w-8 h-8 rounded-full bg-periwinkle-500/10 border hairline border-periwinkle-500 flex items-center justify-center shrink-0">
                <Sparkles className="w-3.5 h-3.5 text-periwinkle-800" />
            </div>
            <div className="flex-1 space-y-3 min-w-0">
                {m.auto_brand_ref && (
                    <div
                        data-testid={`auto-brand-ref-pill-${m.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full glass border hairline border-periwinkle-500/60 text-periwinkle-800 px-2.5 py-1 font-mono text-[10px] tracking-widest uppercase"
                        title="Sin referencia propia: el sistema usó el logo de tu marca como anclaje de identidad."
                    >
                        <Sparkles className="w-3 h-3" />
                        AUTO · LOGO DE MARCA
                    </div>
                )}
                {m.refined_prompt && (
                    <details className="group">
                        <summary className="font-mono text-[10px] text-fog-400 tracking-widest uppercase cursor-pointer hover:text-periwinkle-800 select-none">
                            Prompt refinado ↓
                        </summary>
                        <div className="mt-2 rounded-xl border hairline border-ink-600 bg-ink-900/60 p-3 flex items-start gap-2">
                            <p className="text-xs text-fog-300 font-mono leading-relaxed flex-1">
                                {m.refined_prompt}
                            </p>
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(m.refined_prompt);
                                    toast.success("Prompt copiado");
                                }}
                                className="text-fog-400 hover:text-periwinkle-800 shrink-0"
                                aria-label="Copiar prompt"
                            >
                                <Copy className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </details>
                )}
                {m.role === "assistant" && (userSettings || m.model_id) && (
                    <SettingsStrip
                        settings={userSettings}
                        message={m}
                        modelsCatalog={modelsCatalog}
                        patternsCatalog={patternsCatalog}
                    />
                )}
                {m.error && (
                    <div className="rounded-xl border hairline border-destructive/40 bg-destructive/5 px-3 py-2.5 flex items-start gap-2">
                        <p className="text-sm text-destructive flex-1 leading-snug">
                            {(m.text || "Error generando imagen.").substring(0, 240)}
                            {(m.text || "").length > 240 && "…"}
                        </p>
                        {userSettings && onRetry && (
                            <button
                                data-testid={`retry-${m.id}`}
                                onClick={() =>
                                    onRetry(userText || "", userSettings)
                                }
                                className="shrink-0 rounded-full bg-destructive/10 hover:bg-destructive/20 text-destructive px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors"
                                title="Recarga el prompt y los ajustes en el composer para relanzar"
                            >
                                <RefreshCw className="w-3.5 h-3.5" />
                                Reintentar
                            </button>
                        )}
                    </div>
                )}
                {m.status === "generating" && (
                    <CarouselProgress
                        completed={m.completed_slides || 0}
                        failed={m.failed_slides || 0}
                        total={m.total_slides || 1}
                        startedAt={m.created_at}
                    />
                )}
                {m.status === "generating" &&
                    (m.total_slides || 1) > (m.images?.length || 0) && (
                        <div
                            className={`grid gap-3 ${
                                (m.total_slides || 1) > 1
                                    ? "grid-cols-2 max-w-md"
                                    : "grid-cols-1 max-w-sm"
                            }`}
                        >
                            {Array.from({
                                length:
                                    (m.total_slides || 1) - (m.images?.length || 0),
                            }).map((_, i) => {
                                // Slides are 1-indexed in the DB; the
                                // first skeleton corresponds to the
                                // next un-completed position. We use
                                // `completed_slides + i + 1` so the
                                // partial preview lands on the right
                                // tile when refs are processed in
                                // order. Single-image messages always
                                // show position 1.
                                const pos = (m.completed_slides || 0) + i + 1;
                                const previewPath =
                                    m.slide_previews?.[String(pos)] ||
                                    m.slide_previews?.[pos] ||
                                    null;
                                return (
                                    <SkeletonTile
                                        key={`skel-${m.id}-${i}`}
                                        index={i}
                                        previewPath={previewPath}
                                    />
                                );
                            })}
                        </div>
                    )}
                {m.status === "partial" && m.text && (
                    <p className="text-sm text-amber-400">{m.text}</p>
                )}
                {m.images && m.images.length > 0 && (
                    <div className="flex flex-wrap items-start gap-4">
                        {(refsMeta.length > 0 || apoyosMeta.length > 0) && (
                            <InspirationStrip
                                messageId={m.id}
                                refs={refsMeta}
                                apoyos={apoyosMeta}
                                onOpenAll={() => setInspirationOpen(true)}
                            />
                        )}
                        {m.kind === "resize" ? (
                            <ResizePair
                                images={m.images}
                                onEditZone={onEditZone}
                                onResize={onResize}
                            />
                        ) : (
                            <div
                                className={`grid gap-3 ${
                                    m.images.length > 1
                                        ? "grid-cols-2 max-w-md"
                                        : "grid-cols-1 max-w-sm"
                                }`}
                            >
                                {[...m.images]
                                    .sort(
                                        (a, b) =>
                                            (a.carousel_position || 0) -
                                            (b.carousel_position || 0),
                                    )
                                    .map((img) => (
                                        <GeneratedImage
                                            key={img.library_id}
                                            img={img}
                                            completedAt={m.completed_at || m.created_at}
                                            onEditZone={() => onEditZone?.(img)}
                                            onResize={() => onResize?.(img)}
                                            chatId={chatId}
                                            chatTitle={chatTitle}
                                            initialRating={myRatings?.ratingFor(img.library_id) || null}
                                            onRatingChange={(value, fb) =>
                                                myRatings?.setRating(img.library_id, value, fb)
                                            }
                                            eager={isLatestAssistant}
                                            aspectW={
                                                img.resize_target_width ||
                                                userSettings?.width ||
                                                m.settings?.width ||
                                                1
                                            }
                                            aspectH={
                                                img.resize_target_height ||
                                                userSettings?.height ||
                                                m.settings?.height ||
                                                1
                                            }
                                        />
                                    ))}
                            </div>
                        )}
                    </div>
                )}
                {inspirationOpen && (
                    <InspirationModal
                        refs={refsMeta}
                        apoyos={apoyosMeta}
                        onClose={() => setInspirationOpen(false)}
                    />
                )}
            </div>
        </div>
    );
}

function CarouselProgress({ completed, failed, total, startedAt }) {
    const pct = total > 0 ? Math.round(((completed + failed) / total) * 100) : 0;
    const isCarousel = total > 1;
    // Time-based step indicator for single-image generations. While the
    // backend is grinding away we cycle through realistic micro-steps so
    // the user knows the platform is actively working — replaces the
    // anxiety-inducing blank spinner with concrete feedback.
    const startTs = startedAt
        ? new Date(startedAt).getTime()
        : Date.now();
    const [elapsed, setElapsed] = useState(0);
    useEffect(() => {
        if (isCarousel) return; // carousels already have per-slide signal
        const tick = () =>
            setElapsed(Math.max(0, Math.floor((Date.now() - startTs) / 1000)));
        tick();
        const t = setInterval(tick, 500);
        return () => clearInterval(t);
    }, [isCarousel, startTs]);
    // Each step maps to "expected seconds elapsed". Tuned for ~25-45s
    // typical Nano Banana Pro single-image generation. After the last
    // step the bar pulses indefinitely so 60s+ generations don't look
    // stuck — the work eventually completes via polling.
    const STEPS = [
        { at: 0,  label: "Recibiendo mensaje" },
        { at: 3,  label: "Analizando referencias" },
        { at: 7,  label: "Construyendo prompt" },
        { at: 12, label: "Creando imagen" },
        { at: 30, label: "Revisando resultado" },
        { at: 45, label: "Comprimiendo imagen" },
        { at: 55, label: "Casi listo…" },
    ];
    const currentStep =
        STEPS.slice().reverse().find((s) => elapsed >= s.at) || STEPS[0];
    // Visual fill: scale elapsed against an expected 35s budget but cap
    // at 95% until status flips to complete — that final 5% only fires
    // when the polling loop confirms the message landed.
    const singleProgress = isCarousel
        ? null
        : Math.min(95, Math.round((elapsed / 35) * 100));
    return (
        <div
            data-testid="carousel-progress"
            className="rounded-xl border hairline border-periwinkle-500/40 bg-periwinkle-500/5 px-4 py-3 flex items-center gap-4"
        >
            <div className="relative w-10 h-10 shrink-0">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                    <circle
                        cx="18"
                        cy="18"
                        r="15"
                        fill="none"
                        stroke="rgba(165,166,255,0.18)"
                        strokeWidth="3"
                    />
                    <circle
                        cx="18"
                        cy="18"
                        r="15"
                        fill="none"
                        stroke="#a5a6ff"
                        strokeWidth="3"
                        strokeDasharray={
                            isCarousel
                                ? `${(pct * 94.25) / 100} 94.25`
                                : `${(singleProgress * 94.25) / 100} 94.25`
                        }
                        strokeLinecap="round"
                        style={{ transition: "stroke-dasharray 600ms ease-out" }}
                    />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center font-mono text-[10px] text-periwinkle-800 tabular-nums">
                    {isCarousel ? `${pct}%` : `${singleProgress}%`}
                </span>
            </div>
            <div className="flex-1 min-w-0">
                <p className="font-mono text-[10px] tracking-[0.2em] text-periwinkle-800 uppercase">
                    {isCarousel ? "Generando carrusel" : currentStep.label}
                </p>
                <p className="text-sm text-fog-100 font-medium mt-0.5">
                    {isCarousel ? (
                        <>
                            {completed} de {total} slides listas
                            {failed > 0 && (
                                <span className="text-destructive ml-1">
                                    · {failed} con error
                                </span>
                            )}
                        </>
                    ) : (
                        <>
                            La plataforma está trabajando
                            <span className="inline-block ml-1 w-4 text-left">
                                <span className="animate-pulse-dots">…</span>
                            </span>
                        </>
                    )}
                </p>
                {/* Linear progress bar that mirrors the circle — easier to
                  * read at a glance while a single image is generating. */}
                {!isCarousel && (
                    <div className="mt-2 h-1 rounded-full bg-ink-700 overflow-hidden">
                        <div
                            className="h-full bg-periwinkle-500 transition-[width] duration-500 ease-out"
                            style={{ width: `${singleProgress}%` }}
                        />
                    </div>
                )}
                <p className="text-[11px] text-fog-400 mt-1">
                    {isCarousel
                        ? "La generación puede tardar 15–40 s por slide · llamadas en paralelo"
                        : elapsed >= 55
                          ? `Modelos lentos pueden tardar hasta 2 min · ${elapsed}s transcurridos`
                          : `Suele demorar 15–40 s · ${elapsed}s transcurridos`}
                </p>
            </div>
        </div>
    );
}

/**
 * Animated placeholder shown while an image is being generated. Uses a
 * shimmer gradient + subtle spinner + "Generando" badge so the user
 * knows the model is working. Aspect is square by default (matches the
 * most common 1:1 output); the real image replaces it once it arrives.
 */
function SkeletonTile({ index = 0, previewPath = null }) {
    // Stagger the shimmer when multiple tiles render at once so they
    // don't pulse in perfect sync (more organic feel).
    const delay = `${(index % 4) * 0.2}s`;
    return (
        <div
            data-testid="generating-skeleton"
            className="relative aspect-square rounded-2xl overflow-hidden border hairline border-periwinkle-500/25 bg-ink-900"
        >
            {/* Streaming partial preview from OpenAI gpt-image-2.
              * Sits behind the shimmer with a strong blur so the user
              * sees a "polaroid developing" effect — the final image
              * replaces this when status flips to complete. */}
            {previewPath && (
                <img
                    src={thumbUrl(previewPath, 480)}
                    alt=""
                    data-testid="skeleton-partial-preview"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ filter: "blur(18px) saturate(1.1)", transform: "scale(1.06)" }}
                />
            )}
            {/* Shimmer base */}
            <div
                className={`absolute inset-0 ${previewPath ? "opacity-30 mix-blend-overlay" : ""} skel-shimmer`}
                style={{ animationDelay: delay }}
            />
            {/* Subtle grid pattern to hint "image area" */}
            <div
                className="absolute inset-0 opacity-[0.04]"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)",
                    backgroundSize: "32px 32px",
                }}
            />
            {/* Center badge: spinner + label */}
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <div className="relative w-8 h-8">
                    <svg
                        viewBox="0 0 36 36"
                        className="w-full h-full"
                        style={{ animation: "skel-spin 1.1s linear infinite" }}
                    >
                        <circle
                            cx="18"
                            cy="18"
                            r="15"
                            fill="none"
                            stroke="rgba(165,166,255,0.15)"
                            strokeWidth="3"
                        />
                        <circle
                            cx="18"
                            cy="18"
                            r="15"
                            fill="none"
                            stroke="#a5a6ff"
                            strokeWidth="3"
                            strokeDasharray="23.5 94.25"
                            strokeLinecap="round"
                        />
                    </svg>
                </div>
                <span className="font-mono text-[9px] tracking-[0.25em] text-periwinkle-800 uppercase">
                    Generando…
                </span>
            </div>
        </div>
    );
}

/**
 * Compact vertical strip shown next to a generated image — displays up to
 * 3 thumbnails (refs + apoyos interleaved by priority: refs first) with
 * the right accent color per kind. When there are more than 3 items we
 * show "+N más" that opens the full InspirationModal.
 */

/**
 * Inline metadata strip surfaced under the refined-prompt block so the
 * user knows exactly which settings produced each image: model (friendly
 * name), resolution (WxH), creativity level, patterns used, and carousel
 * size. Reads from the paired user message's `settings` snapshot plus the
 * assistant's `model_id` (which reflects the auto-swap when it kicks in).
 */
function SettingsStrip({ settings, message, modelsCatalog = [], patternsCatalog = [] }) {
    if (!settings && !message) return null;

    // Resolve model label from the catalog (e.g. "GPT Image 2 · Edit").
    // Prefer the model actually used (assistant.model_id, which reflects
    // auto-swaps) and fall back to the user-requested id.
    const modelId = message?.model_id || settings?.model_id;
    const modelEntry = (modelsCatalog || []).find((x) => x.id === modelId);
    const modelName = modelEntry?.name || modelId || "—";

    const width = settings?.width;
    const height = settings?.height;

    const creativityMap = {
        low: "Baja",
        medium: "Media",
        high: "Alta",
    };
    const creativity = settings?.creativity_level
        ? creativityMap[settings.creativity_level] || settings.creativity_level
        : null;

    const patternIds = Array.isArray(settings?.patterns) ? settings.patterns : [];
    const patternNames = patternIds
        .map((pid) => {
            const p = (patternsCatalog || []).find((x) => x.id === pid);
            return p?.name || pid;
        })
        .filter(Boolean);

    const numImages = settings?.num_images || message?.total_slides || 1;

    // Detect when the assistant actually used a different model than the
    // one the user selected (auto-swap from /edit → t2i when no refs).
    const swapped = modelId && settings?.model_id && modelId !== settings.model_id;
    const originalRequest = swapped
        ? (modelsCatalog || []).find((x) => x.id === settings.model_id)?.name ||
          settings.model_id
        : null;

    return (
        <div
            data-testid={`message-settings-${message?.id || "x"}`}
            className="rounded-xl border hairline border-ink-600 bg-ink-900/40 px-3 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5"
        >
            <span className="font-mono text-[9px] tracking-[0.25em] text-fog-500 uppercase">
                CONFIG
            </span>

            <SettingField label="Modelo" value={modelName} testId="config-model" />
            {swapped && (
                <SettingField
                    label="Cambio auto"
                    value={`desde ${originalRequest}`}
                    tone="amber"
                    testId="config-swap"
                />
            )}
            {width && height && (
                <SettingField
                    label="Resolución"
                    value={`${width}×${height}`}
                    testId="config-size"
                />
            )}
            {creativity && (
                <SettingField
                    label="Creatividad"
                    value={creativity}
                    testId="config-creativity"
                />
            )}
            {numImages > 1 && (
                <SettingField
                    label="Carrusel"
                    value={`×${numImages}`}
                    testId="config-carousel"
                />
            )}
            {patternNames.length > 0 && (
                <SettingField
                    label="Patrones"
                    value={patternNames.join(" · ")}
                    testId="config-patterns"
                />
            )}
        </div>
    );
}

function SettingField({ label, value, tone = "default", testId }) {
    const toneClass =
        tone === "amber"
            ? "text-amber-400"
            : "text-fog-100";
    return (
        <div className="flex items-baseline gap-1.5" data-testid={testId}>
            <span className="font-mono text-[9px] tracking-[0.2em] text-fog-500 uppercase">
                {label}
            </span>
            <span className={`font-mono text-[11px] ${toneClass}`}>{value}</span>
        </div>
    );
}


function InspirationStrip({ messageId, refs, apoyos, onOpenAll }) {
    const MAX = 3;
    const items = [
        ...refs.map((r) => ({ ...r, _kind: "ref" })),
        ...apoyos.map((a) => ({ ...a, _kind: "apoyo" })),
    ];
    const visible = items.slice(0, MAX);
    const extra = items.length - visible.length;
    const hasRefs = refs.length > 0;
    const hasApoyos = apoyos.length > 0;

    return (
        <div
            data-testid={`generation-refs-${messageId}`}
            className="shrink-0 flex flex-col gap-2"
        >
            <p className="font-mono text-[9px] tracking-[0.25em] uppercase">
                {hasRefs && hasApoyos ? (
                    <span className="text-fog-400">Inspiración</span>
                ) : hasRefs ? (
                    <span className="text-periwinkle-800">Referencia base</span>
                ) : (
                    <span className="text-amber-300">Apoyos</span>
                )}
            </p>
            {visible.map((r) => (
                <InspirationThumb key={r.id} item={r} />
            ))}
            {extra > 0 && (
                <button
                    type="button"
                    data-testid={`inspiration-see-all-${messageId}`}
                    onClick={onOpenAll}
                    className="w-24 h-12 rounded-lg border hairline border-ink-600 bg-ink-900 text-fog-300 hover:text-periwinkle-800 hover:border-periwinkle-500 transition-colors font-mono text-[10px] tracking-widest uppercase"
                >
                    + {extra} {extra === 1 ? "más" : "más"}
                </button>
            )}
        </div>
    );
}

/**
 * Single thumbnail that auto-styles by its `_kind`:
 *   - "ref"   → teal border + "Referencia" caption (portfolio)
 *   - "apoyo" → amber border + "Apoyo" caption (chat upload)
 */
function InspirationThumb({ item, size = 96 }) {
    const isRef = item._kind === "ref";
    const border = isRef ? "border-periwinkle-500/60 hover:border-periwinkle-500" : "border-amber-400/60 hover:border-amber-400";
    const captionBg = isRef
        ? "bg-periwinkle-500/90 text-periwinkle-ink"
        : "bg-amber-400/85 text-ink-on";
    const caption = isRef ? "Referencia" : "Apoyo";
    return (
        <a
            href={fileUrl(item.storage_path)}
            target="_blank"
            rel="noreferrer"
            className={`block rounded-lg overflow-hidden border hairline transition-colors relative ${border}`}
            style={{ width: size, height: size }}
            title={item.title || item.original_filename || caption}
        >
            <RetryImage
                src={thumbUrl(item.storage_path, 160)}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover"
            />
            <span
                className={`absolute bottom-0 left-0 right-0 font-mono text-[7px] tracking-widest text-center uppercase py-0.5 ${captionBg}`}
                aria-hidden="true"
            >
                {caption}
            </span>
        </a>
    );
}

/**
 * Modal that surfaces EVERY piece of inspiration used for a generation —
 * referencias (portfolio, teal) in one section and apoyos (chat uploads,
 * amber) in another. Useful when a prompt pulls in 4+ pieces and the chat
 * strip only shows a preview of the first 3.
 */
function InspirationModal({ refs, apoyos, onClose }) {
    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [onClose]);

    return (
        <div
            data-testid="inspiration-modal"
            className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl border hairline border-ink-600 bg-ink-900 shadow-glow">
                <header className="sticky top-0 z-10 flex items-center gap-3 px-6 py-4 border-b hairline border-ink-600 bg-ink-900/95 backdrop-blur">
                    <Sparkles className="w-5 h-5 text-periwinkle-800" />
                    <div className="flex-1 min-w-0">
                        <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase">
                            Inspiración usada
                        </p>
                        <h2 className="font-display text-2xl text-fog-100 leading-tight">
                            {refs.length + apoyos.length} imágenes
                        </h2>
                    </div>
                    <button
                        data-testid="inspiration-modal-close"
                        onClick={onClose}
                        className="w-9 h-9 rounded-full border hairline border-ink-600 text-fog-300 hover:text-fog-100 hover:border-periwinkle-500 flex items-center justify-center"
                        aria-label="Cerrar"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </header>
                <div className="p-6 space-y-6">
                    {refs.length > 0 && (
                        <section>
                            <p className="font-mono text-[10px] tracking-[0.3em] text-periwinkle-800 uppercase mb-3">
                                Referencias · punto base ({refs.length})
                            </p>
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                                {refs.map((r) => (
                                    <InspirationThumb
                                        key={r.id}
                                        item={{ ...r, _kind: "ref" }}
                                        size={130}
                                    />
                                ))}
                            </div>
                        </section>
                    )}
                    {apoyos.length > 0 && (
                        <section>
                            <p className="font-mono text-[10px] tracking-[0.3em] text-amber-300 uppercase mb-3">
                                Apoyos · uploads del chat ({apoyos.length})
                            </p>
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                                {apoyos.map((a) => (
                                    <InspirationThumb
                                        key={a.id}
                                        item={{ ...a, _kind: "apoyo" }}
                                        size={130}
                                    />
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
}

function GeneratedImage({ img, completedAt, onEditZone, onResize, chatId, chatTitle, initialRating, onRatingChange, eager = false, aspectW = 1, aspectH = 1 }) {
    const [fav, setFav] = useState(false);
    const [downloading, setDownloading] = useState(false);
    // Format the generation duration (e.g. "1 min 6 s" / "47 s") and the
    // local datetime when it finished. Both come from the assistant
    // message — duration is per-slide (stored on the image), date is
    // shared across the message. Old messages (pre-iter86) won't have
    // generation_seconds, so we render only the date in that case.
    const meta = (() => {
        const secs = Number(img.generation_seconds);
        const hasSecs = Number.isFinite(secs) && secs > 0;
        let durationLabel = null;
        if (hasSecs) {
            if (secs >= 60) {
                const m = Math.floor(secs / 60);
                const s = Math.round(secs - m * 60);
                durationLabel = s > 0 ? `${m} min ${s} s` : `${m} min`;
            } else {
                durationLabel = `${secs.toFixed(secs < 10 ? 1 : 0)} s`;
            }
        }
        let dateLabel = null;
        if (completedAt) {
            try {
                const d = new Date(completedAt);
                if (!Number.isNaN(d.getTime())) {
                    dateLabel = d.toLocaleString("es-CO", {
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                    });
                }
            } catch {
                /* noop */
            }
        }
        return { durationLabel, dateLabel };
    })();
    const toggle = async () => {
        try {
            const r = await api.post(`/library/${img.library_id}/favorite`);
            setFav(r.data.favorite);
        } catch {
            /* noop */
        }
    };
    const download = async () => {
        if (downloading) return;
        setDownloading(true);
        try {
            const res = await fetch(fileUrl(img.storage_path));
            if (!res.ok) throw new Error("fetch failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const ext = (blob.type.split("/")[1] || "png").split(";")[0];
            const safeExt = ["png", "jpeg", "jpg", "webp"].includes(ext) ? (ext === "jpeg" ? "jpg" : ext) : "png";
            const a = document.createElement("a");
            a.href = url;
            a.download = `krea-${img.library_id || Date.now()}.${safeExt}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch {
            toast.error("No se pudo descargar la imagen");
        } finally {
            setDownloading(false);
        }
    };
    return (
        <div className="group relative rounded-xl overflow-hidden border hairline border-ink-600 bg-ink-900">
            <div
                className="relative"
                style={{
                    // Reserve the layout box BEFORE the image lands so
                    // scroll-to-bottom and IntersectionObserver work
                    // correctly even with lazy-loaded images. Without
                    // this, the placeholder is 1×1, the chat lands above
                    // the actual bottom, and the observer never fires
                    // for off-screen tiles → blank thumbs forever.
                    aspectRatio: `${aspectW} / ${aspectH}`,
                }}
            >
            <RetryImage
                // Render the thumbnail in the chat — 960px wide is plenty
                // for visual review while keeping bandwidth at ~150KB per
                // image (vs 2-8 MB for the full 2K/4K). The full file is
                // still used for the in-place download button below.
                src={thumbUrl(img.storage_path, 960)}
                // Eager ONLY for the latest assistant message — the user
                // is staring at this exact spot waiting for the result,
                // so we skip the observer round-trip. Everything older
                // (entire chat history) stays lazy so we never fire 40+
                // simultaneous thumbnail GETs on chat open. Combined
                // with the aspect-ratio box above, the layout stays
                // stable while images progressively appear as the user
                // scrolls upward.
                lazy={!eager}
                alt="generada"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            />
            <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{
                    background:
                        "linear-gradient(to top, rgba(10,14,42,0.65), transparent 55%)",
                }}
            />
            {img.edit_count > 1 && (
                <span
                    data-testid={`generated-edit-count-${img.library_id}`}
                    className="absolute top-2 left-2 rounded-full bg-periwinkle-500 text-periwinkle-ink px-2 py-0.5 font-mono text-[9px] tracking-widest uppercase inline-flex items-center gap-1 shadow-glow-sm"
                    title={`Esta imagen ha sido editada ${img.edit_count - 1} vez(es). Abre el editor para ver el historial.`}
                >
                    <span className="w-1 h-1 rounded-full bg-white" />
                    v{img.edit_count}
                </span>
            )}
            {img.edited_from_library_id && !img.edit_count && (
                <span
                    data-testid={`generated-edited-badge-${img.library_id}`}
                    className="absolute top-2 left-2 rounded-full glass border hairline border-periwinkle-500/60 text-periwinkle-800 px-2 py-0.5 font-mono text-[9px] tracking-widest uppercase"
                    title="Imagen editada por zona"
                >
                    Editada
                </span>
            )}
            <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                    data-testid={`generated-fav-${img.library_id}`}
                    onClick={toggle}
                    className={`w-8 h-8 rounded-full glass border hairline border-ink-600 flex items-center justify-center ${
                        fav ? "text-periwinkle-800" : "text-fog-300"
                    }`}
                    aria-label="Favorito"
                >
                    <Heart className="w-4 h-4" fill={fav ? "currentColor" : "none"} />
                </button>
                {onResize && (
                    <button
                        data-testid={`generated-resize-${img.library_id}`}
                        onClick={onResize}
                        className="w-8 h-8 rounded-full glass border hairline border-ink-600 text-fog-300 hover:text-periwinkle-800 hover:border-periwinkle-500 flex items-center justify-center transition-colors"
                        aria-label="Redimensionar"
                        title="Redimensionar a otros formatos"
                    >
                        <Maximize2 className="w-4 h-4" />
                    </button>
                )}
                <button
                    data-testid={`generated-download-${img.library_id}`}
                    onClick={download}
                    disabled={downloading}
                    className="w-8 h-8 rounded-full glass border hairline border-ink-600 text-fog-300 hover:text-periwinkle-800 hover:border-periwinkle-500 flex items-center justify-center transition-colors disabled:opacity-50"
                    aria-label="Descargar imagen"
                    title="Descargar"
                >
                    <Download className="w-4 h-4" />
                </button>
                <button
                    data-testid={`generated-edit-zone-${img.library_id}`}
                    onClick={onEditZone}
                    className="w-8 h-8 rounded-full glass border hairline border-ink-600 text-fog-300 hover:text-periwinkle-800 hover:border-periwinkle-500 flex items-center justify-center transition-colors"
                    aria-label="Editar imagen específica"
                    title="Editar imagen específica (zona)"
                >
                    <Pencil className="w-4 h-4" />
                </button>
                <a
                    href={fileUrl(img.storage_path)}
                    target="_blank"
                    rel="noreferrer"
                    className="w-8 h-8 rounded-full glass border hairline border-ink-600 flex items-center justify-center text-fog-300 font-mono text-[10px]"
                    title="Abrir"
                >
                    ↗
                </a>
            </div>
            </div>
            {(meta.durationLabel || meta.dateLabel) && (
                <div
                    data-testid={`generated-meta-${img.library_id}`}
                    className="flex items-center justify-between gap-2 px-3 py-2 border-t hairline border-ink-700 bg-ink-900/60"
                    title={
                        meta.durationLabel && meta.dateLabel
                            ? `Generada en ${meta.durationLabel} · ${meta.dateLabel}`
                            : meta.durationLabel || meta.dateLabel
                    }
                >
                    {meta.durationLabel ? (
                        <span className="font-mono text-[10px] tracking-widest uppercase text-periwinkle-800">
                            {meta.durationLabel}
                        </span>
                    ) : (
                        <span />
                    )}
                    {meta.dateLabel && (
                        <span className="font-mono text-[10px] tracking-widest uppercase text-fog-400">
                            {meta.dateLabel}
                        </span>
                    )}
                </div>
            )}
            <div className="px-3 py-2 border-t hairline border-ink-700 bg-ink-900/40 flex items-center justify-between gap-2">
                <span className="font-mono text-[9px] tracking-widest uppercase text-fog-500">
                    ¿Qué te pareció?
                </span>
                <RatingButtons
                    itemType="image"
                    itemId={img.library_id}
                    chatId={chatId}
                    chatTitle={chatTitle}
                    itemPreview={img.storage_path}
                    initial={initialRating}
                    onChange={onRatingChange}
                />
            </div>
        </div>
    );
}

function ModelDropdown({ models, value, onChange, currency = "USD", trm = 4000 }) {
    const groups = models.reduce((acc, m) => {
        const key = m.family || "flux";
        (acc[key] = acc[key] || []).push(m);
        return acc;
    }, {});
    const orderedFamilies = ["flux", "nano-banana", "gpt-image"].filter(
        (f) => groups[f],
    );
    const labels = {
        flux: "FLUX",
        "nano-banana": "Nano Banana",
        "gpt-image": "GPT Image",
    };
    /** Format the per-model price as `$X.XX USD` or `$X.XXX COP` for inline use. */
    const fmtPrice = (cop) => {
        if (cop === undefined || cop === null) return "";
        if (currency === "USD") {
            const usd = trm > 0 ? cop / trm : 0;
            return ` · $${usd.toFixed(2)} USD`;
        }
        return ` · $${new Intl.NumberFormat("es-CO", {
            maximumFractionDigits: 0,
        }).format(cop)} COP`;
    };
    return (
        <select
            data-testid="chat-model-picker"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="rounded-full bg-ink-900/60 border hairline border-ink-600 text-fog-100 px-3 py-1.5 text-xs outline-none focus:border-periwinkle-500 hover:border-periwinkle-500 transition-colors max-w-[280px]"
        >
            {orderedFamilies.map((fam) => (
                <optgroup key={fam} label={labels[fam]}>
                    {groups[fam].map((m) => (
                        <option key={m.id} value={m.id}>
                            {m.name}
                            {m.supports_image_input ? " · img ref" : ""}
                            {fmtPrice(m.cost_per_image_cop)}
                        </option>
                    ))}
                </optgroup>
            ))}
        </select>
    );
}

function ResolutionPicker({ resolutions, width, height, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const current =
        resolutions.find((r) => r.width === width && r.height === height) ||
        resolutions[0];

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    return (
        <div ref={ref}>
            <p className="font-mono text-[10px] tracking-[0.25em] text-fog-400 uppercase mb-2">
                Resolución
            </p>
            <div className="relative">
                <button
                    type="button"
                    data-testid="chat-config-resolution"
                    onClick={() => setOpen((v) => !v)}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    className={`w-full flex items-center gap-3 rounded-xl border hairline bg-ink-900 px-3 py-2.5 text-left transition-colors ${
                        open
                            ? "border-periwinkle-500"
                            : "border-ink-600 hover:border-periwinkle-500/60"
                    }`}
                >
                    <AspectIcon width={current.width} height={current.height} />
                    <span className="flex-1 min-w-0">
                        <span className="block text-sm text-fog-100 truncate font-medium">
                            {current.label}
                        </span>
                        <span className="block font-mono text-[9px] text-fog-400">
                            {current.width}×{current.height} px
                        </span>
                    </span>
                    <ChevronDown
                        className={`w-4 h-4 text-fog-400 transition-transform ${
                            open ? "rotate-180" : ""
                        }`}
                    />
                </button>
                {open && (
                    <div
                        role="listbox"
                        data-testid="chat-config-resolution-list"
                        className="absolute z-30 left-0 right-0 mt-1.5 rounded-xl border hairline border-ink-600 bg-ink-900 shadow-glow-sm overflow-hidden max-h-80 overflow-y-auto animate-fade-up"
                    >
                        {resolutions.map((r) => {
                            const active =
                                r.width === current.width && r.height === current.height;
                            return (
                                <button
                                    key={r.id}
                                    type="button"
                                    role="option"
                                    aria-selected={active}
                                    data-testid={`res-${r.id}`}
                                    onClick={() => {
                                        onChange(r);
                                        setOpen(false);
                                    }}
                                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                                        active
                                            ? "bg-periwinkle-500/10 text-periwinkle-800"
                                            : "text-fog-200 hover:bg-ink-800 hover:text-fog-100"
                                    }`}
                                >
                                    <AspectIcon width={r.width} height={r.height} />
                                    <span className="flex-1 min-w-0">
                                        <span
                                            className={`block text-sm truncate ${
                                                active ? "font-semibold" : ""
                                            }`}
                                        >
                                            {r.label}
                                        </span>
                                        <span className="block font-mono text-[9px] text-fog-400">
                                            {r.width}×{r.height} px
                                        </span>
                                    </span>
                                    {active && (
                                        <span
                                            className="w-1.5 h-1.5 rounded-full bg-periwinkle-500"
                                            aria-hidden="true"
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * Small aspect-ratio glyph: an outlined rectangle whose box matches the
 * chosen width:height. Used in the resolution dropdown preview so the user
 * can eyeball portrait vs square vs landscape at a glance.
 */
function AspectIcon({ width, height }) {
    const MAX = 22;
    const ratio = width / height;
    const w = ratio >= 1 ? MAX : Math.round(MAX * ratio);
    const h = ratio >= 1 ? Math.round(MAX / ratio) : MAX;
    return (
        <span
            aria-hidden="true"
            className="inline-flex items-center justify-center w-6 h-6 shrink-0"
        >
            <span
                className="block border hairline border-periwinkle-500 rounded-[3px] bg-periwinkle-500/15"
                style={{ width: `${w}px`, height: `${h}px` }}
            />
        </span>
    );
}

function CarouselPicker({ value, onChange }) {
    // 1 = single image, 2..8 = carousel (cada slide se cobra por separado).
    return (
        <div>
            <p className="font-mono text-[10px] tracking-[0.25em] text-fog-400 uppercase mb-2">
                Cantidad de imágenes
            </p>
            <div className="space-y-2">
                <div className="flex gap-2 flex-wrap">
                    <SlotBtn
                        label="1"
                        sub="pieza única"
                        selected={value === 1}
                        onClick={() => onChange(1)}
                        testId="count-1"
                    />
                </div>
                <div>
                    <p className="font-mono text-[9px] tracking-[0.22em] text-fog-500 uppercase mb-1.5">
                        Carrusel (2-8 · cada slide se cobra aparte)
                    </p>
                    <div className="grid grid-cols-7 gap-1.5">
                        {[2, 3, 4, 5, 6, 7, 8].map((n) => (
                            <SlotBtn
                                key={n}
                                label={n}
                                selected={value === n}
                                onClick={() => onChange(n)}
                                testId={`count-${n}`}
                                small
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function SlotBtn({ label, sub, selected, onClick, testId, small = false }) {
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={onClick}
            className={`rounded-lg border hairline transition-all flex flex-col items-center justify-center ${
                small ? "py-2 text-xs" : "px-4 py-2 text-sm"
            } ${
                selected
                    ? "border-periwinkle-500 text-periwinkle-800 bg-periwinkle-500/10"
                    : "border-ink-600 text-fog-300 hover:text-fog-100 hover:border-ink-500"
            }`}
        >
            <span className="font-display leading-none">{label}</span>
            {sub && (
                <span className="font-mono text-[9px] tracking-widest uppercase text-fog-500 mt-0.5">
                    {sub}
                </span>
            )}
        </button>
    );
}

/**
 * Aspect-ratio presets for the resize dialog. The IDs map to the
 * (width, height) pixel dimensions sent to the backend. The pixel values
 * are a hint to the backend — when the source image is larger than the
 * preset, the backend will scale UP to match the source max-side
 * (preserving the ratio) so resize never degrades resolution.
 *
 * The UI deliberately surfaces only the three universal layouts; deeper
 * options were removed to keep the picker focused on the most common
 * choices our users actually need.
 */
const RESIZE_PRESETS = [
    { id: "1x1",   label: "Cuadrado",   ratio: "1:1",   w: 1024, h: 1024, hint: "Instagram post · perfil" },
    { id: "4x5",   label: "Vertical",   ratio: "4:5",   w: 1080, h: 1350, hint: "Stories · feed retrato" },
    { id: "16x9",  label: "Horizontal", ratio: "16:9",  w: 1920, h: 1080, hint: "YouTube · web banner" },
];

/**
 * Render the resize result message: source on the left, generated variant
 * on the right, both with a small label so the user can tell which is which.
 *
 * Falls back to a single-image grid if the message is malformed.
 */
function ResizePair({ images, onEditZone, onResize }) {
    const base = images.find((i) => i.role === "resize_base") || images[0];
    const variant = images.find((i) => i.role === "resize_variant") || images[1];
    if (!variant) {
        return (
            <GeneratedImage
                img={base}
                onEditZone={() => onEditZone?.(base)}
                onResize={() => onResize?.(base)}
            />
        );
    }
    return (
        <div className="grid grid-cols-2 gap-3 max-w-2xl">
            <ResizeSlot
                img={base}
                label="Original"
                allowResize={false}
                onEditZone={() => onEditZone?.(base)}
                onResize={() => onResize?.(base)}
            />
            <ResizeSlot
                img={variant}
                label={
                    variant.resize_format_label ||
                    `${variant.resize_target_width || ""}×${variant.resize_target_height || ""}`
                }
                allowResize={true}
                onEditZone={() => onEditZone?.(variant)}
                onResize={() => onResize?.(variant)}
            />
        </div>
    );
}

/** A single tile inside the resize pair. Tiny label sticky at top-left. */
function ResizeSlot({ img, label, allowResize, onEditZone, onResize }) {
    return (
        <div className="relative">
            <span
                className="absolute z-10 top-2 left-2 rounded-full bg-ink-950/80 backdrop-blur border hairline border-ink-600 text-fog-200 px-2 py-0.5 font-mono text-[9px] tracking-widest uppercase"
                title={label}
            >
                {label}
            </span>
            <GeneratedImage
                img={img}
                onEditZone={onEditZone}
                onResize={allowResize ? onResize : undefined}
                aspectW={img.resize_target_width || img.width || 1}
                aspectH={img.resize_target_height || img.height || 1}
            />
        </div>
    );
}

/**
 * Modal: lists every resize preset. Two flavours:
 *  - `target` mode (in-chat): receives `{chatId, messageId, img}` and hits
 *    `/chats/{cid}/messages/{mid}/images/{lid}/resize`. The new variant is
 *    returned as a fresh assistant message that the chat appends.
 *  - `source` mode (standalone): receives a picked image (file_id + storage_path
 *    + maybe library_id / reference_id) and hits `/library/resize-source`. The
 *    result lands directly in /library — no chat message is created.
 *
 * In-chat mode keeps the dialog open after success so picking another preset
 * for the same source takes one click. Standalone mode closes after the first
 * generation because the user is no longer reading the chat thread.
 */
function ResizeDialog({ target, source, chatId, onClose, onResolved }) {
    const [pending, setPending] = useState(null); // preset id while in-flight
    const isStandalone = Boolean(source);
    const sourceLabel = isStandalone
        ? source.title || source.original_filename || "Imagen base"
        : "Imagen del chat";
    const sourceThumb = isStandalone ? thumbUrl(source.storage_path, 160) : null;

    const generate = async (preset) => {
        if (pending) return;
        setPending(preset.id);
        try {
            if (isStandalone) {
                // Standalone: route through /library/resize-source. The backend
                // resolves the source via library_id → reference_id → file_id
                // depending on which is present, so we send everything we know.
                // Including chat_id lets the new pair appear in the current
                // chat thread; when missing, the backend mints a fresh chat.
                const body = {
                    target_width: preset.w,
                    target_height: preset.h,
                    format_label: `${preset.label} ${preset.ratio}`,
                };
                if (chatId) body.chat_id = chatId;
                if (source.library_id) body.source_library_id = source.library_id;
                if (source.reference_id) body.source_reference_id = source.reference_id;
                if (source.file_id) body.source_file_id = source.file_id;
                if (source.storage_path) body.source_storage_path = source.storage_path;
                const r = await api.post("/library/resize-source", body);
                toast.success(
                    `Versión ${preset.label} lista — agregada al chat`,
                );
                onResolved?.({
                    userMessage: r.data.user_message,
                    message: r.data.message,
                    chatId: r.data.chat_id,
                    createdNewChat: !!r.data.created_new_chat,
                });
                onClose?.();
            } else {
                const r = await api.post(
                    `/chats/${target.chatId}/messages/${target.messageId}/images/${target.img.library_id}/resize`,
                    {
                        target_width: preset.w,
                        target_height: preset.h,
                        format_label: `${preset.label} ${preset.ratio}`,
                    },
                );
                // Backend now returns a placeholder with status="generating".
                // The chat's existing polling loop will pick it up and flip
                // the message to status="complete" with the variant attached
                // once fal.ai finishes (typically 15-60s, but works even
                // when it takes >60s — no 504 because we returned fast).
                toast.success(`Generando versión ${preset.label}…`);
                onResolved?.(r.data.message);
                onClose?.();
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo redimensionar",
            );
        } finally {
            setPending(null);
        }
    };
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/80 backdrop-blur-sm p-4"
            onClick={onClose}
            data-testid="resize-dialog"
        >
            <div
                className="bg-ink-900 border hairline border-ink-600 rounded-2xl max-w-2xl w-full p-6 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-start gap-3 min-w-0">
                        {sourceThumb && (
                            <RetryImage
                                src={sourceThumb}
                                alt={sourceLabel}
                                data-testid="resize-dialog-source-thumb"
                                className="w-14 h-14 rounded-lg object-cover border hairline border-ink-600 shrink-0"
                            />
                        )}
                        <div className="min-w-0">
                            <h3 className="font-display text-xl text-fog-100">
                                Redimensionar imagen
                            </h3>
                            <p className="text-fog-400 text-xs mt-1 line-clamp-2">
                                {isStandalone
                                    ? <>Base: <span className="text-periwinkle-800">{sourceLabel}</span>. La variante aparecerá en el chat junto a la original.</>
                                    : "Genera una versión nueva manteniendo la misma escena y composición — solo cambia el formato."}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="resize-dialog-close"
                        className="text-fog-400 hover:text-fog-100 shrink-0"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {RESIZE_PRESETS.map((p) => {
                        const isPending = pending === p.id;
                        const disabled = pending !== null;
                        return (
                            <button
                                key={p.id}
                                type="button"
                                data-testid={`resize-preset-${p.id}`}
                                onClick={() => generate(p)}
                                disabled={disabled}
                                className={`rounded-xl border hairline p-3 text-left transition-all ${
                                    isPending
                                        ? "border-periwinkle-500 bg-periwinkle-500/10"
                                        : "border-ink-600 hover:border-periwinkle-500 hover:bg-periwinkle-500/5"
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="font-mono text-[10px] tracking-widest uppercase text-periwinkle-800">
                                        {p.ratio}
                                    </span>
                                </div>
                                <p className="font-display text-sm text-fog-100">
                                    {p.label}
                                </p>
                                <p className="font-mono text-[9px] text-fog-500 mt-1">
                                    {isPending ? "Generando…" : p.hint}
                                </p>
                            </button>
                        );
                    })}
                </div>
                <p className="font-mono text-[10px] text-fog-500 mt-4 leading-relaxed">
                    Cada versión usa Nano Banana Pro y consume 1 imagen de tu
                    presupuesto mensual. La fidelidad es estricta — no se añaden
                    ni eliminan elementos.
                </p>
            </div>
        </div>
    );
}

/**
 * Quality preset picker — `basic` | `medium` | `high`. Sits next to the
 * model dropdown in the chat toolbar. Each tier multiplies the requested
 * resolution server-side (×1.0 / ×1.5 / ×2.0) and adds a configurable USD
 * surcharge on top of the model's per-image cost.
 *
 * The current surcharge for the active tier shows as a small chip so the
 * user knows exactly how much extra they're spending before they generate.
 */
function QualityPicker({ value, onChange, surchargeUsd, currency, trm }) {
    const active = value || "basic";
    const fmtSurcharge = (usd) => {
        if (!usd || usd <= 0) return "+0";
        if (currency === "COP") {
            const cop = Math.round(usd * (trm || 4000));
            return `+${new Intl.NumberFormat("es-CO", {
                maximumFractionDigits: 0,
            }).format(cop)} COP`;
        }
        return `+$${usd.toFixed(2)} USD`;
    };
    return (
        <div
            className="inline-flex rounded-full border hairline border-ink-600 bg-ink-900 p-0.5"
            role="group"
            aria-label="Calidad de la imagen"
            data-testid="chat-quality-picker"
        >
            {QUALITY_OPTIONS.map((opt) => {
                const isActive = active === opt.value;
                const extra = surchargeUsd?.[opt.value] ?? 0;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        data-testid={`chat-quality-${opt.value}`}
                        onClick={() => onChange(opt.value)}
                        title={`${opt.label} · ${opt.hint} · ${fmtSurcharge(extra)}`}
                        className={`px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest transition-colors flex items-center gap-1.5 ${
                            isActive
                                ? "bg-periwinkle-500 text-ink-on"
                                : "text-fog-400 hover:text-fog-100"
                        }`}
                    >
                        {opt.label}
                        {extra > 0 && (
                            <span
                                className={`text-[9px] tabular-nums ${
                                    isActive ? "text-ink-on/70" : "text-fog-500"
                                }`}
                            >
                                {fmtSurcharge(extra)}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}


/**
 * Tiny info row that sits under <QualityPicker>. Tells the user, in one
 * line, the projected output resolution (after the quality multiplier)
 * and the total estimated cost (model.cost_per_image_cop + surcharge
 * × current_TRM) in the active currency.
 *
 * `model` is the full model doc from /catalog/models so we can pull its
 * COP cost; pass `null` while it's still loading and the cost slot
 * gracefully hides itself.
 */
function QualitySummary({ quality, width, height, model, surchargeUsd, currency, trm, refCount = 0, apoyoCount = 0, extraRefCostUsd = 0.1 }) {
    const scale = { basic: 1.0, medium: 1.5, high: 2.0 }[quality] || 1.0;
    // Mirror the backend clamp (256..4096) so what the chip shows matches
    // what the provider actually receives after `scale_by_quality`.
    const clamp = (n) => Math.max(256, Math.min(4096, Math.round(n)));
    const projW = clamp((width || 1024) * scale);
    const projH = clamp((height || 1024) * scale);
    const surcharge = Number(surchargeUsd?.[quality] ?? 0);
    // Extra-reference surcharge: first ref + first apoyo are free.
    const extras = Math.max(0, refCount - 1) + Math.max(0, apoyoCount - 1);
    const extrasUsd = extras * Number(extraRefCostUsd || 0);
    const baseCop = Number(model?.cost_per_image_cop || 0);
    const trmNow = Number(trm || 4000);
    const totalCop = baseCop + (surcharge + extrasUsd) * trmNow;
    const fmtCost = () => {
        if (currency === "COP") {
            return `${new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(totalCop)} COP`;
        }
        const totalUsd = trmNow > 0 ? totalCop / trmNow : 0;
        return `$${totalUsd.toFixed(2)} USD`;
    };
    const qualityLabel =
        { basic: "Básico", medium: "Moderado", high: "Súper calidad" }[quality] || "Básico";
    return (
        <div
            data-testid="chat-quality-summary"
            className="flex items-center gap-3 flex-wrap rounded-xl border hairline border-periwinkle-500/25 bg-periwinkle-500/5 px-3.5 py-2"
        >
            <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-periwinkle-800">
                Vista previa · {qualityLabel}
            </span>
            <span
                className="font-mono text-xs text-fog-100 tabular-nums"
                data-testid="chat-quality-summary-resolution"
            >
                Resolución: <span className="text-periwinkle-800">{projW}×{projH}px</span>
            </span>
            {baseCop > 0 && (
                <span
                    className="font-mono text-xs text-fog-100 tabular-nums"
                    data-testid="chat-quality-summary-cost"
                >
                    Costo estimado: <span className="text-periwinkle-800">{fmtCost()}</span>
                </span>
            )}
            {surcharge > 0 && (
                <span className="font-mono text-[10px] text-fog-500 tabular-nums">
                    (modelo + ${surcharge.toFixed(2)} USD por calidad)
                </span>
            )}
            {extras > 0 && (
                <span
                    className="font-mono text-[10px] text-amber-400 tabular-nums"
                    data-testid="chat-quality-summary-extras"
                    title={`${extras} imagen${extras === 1 ? "" : "es"} extra (primera ref. y primer apoyo gratis)`}
                >
                    + {extras} extra{extras === 1 ? "" : "s"} ${extrasUsd.toFixed(2)} USD
                </span>
            )}
        </div>
    );
}


/**
 * Compact one-line summary that replaces the old toolbar. Renders the
 * essentials (model · quality · resolution × multiplier · estimated cost)
 * in a single clickable pill. The whole row opens the configuration
 * popover — designed to leave maximum vertical space for the chat history
 * while still letting the user verify what they're about to generate.
 */
function ConfigSummaryChip({ settings, models, surchargeUsd, currency, trm, refCount = 0, apoyoCount = 0, extraRefCostUsd = 0.1, onClick }) {
    const model = models.find((m) => m.id === settings.model_id);
    const qualityLabel =
        { basic: "Básico", medium: "Moderado", high: "Súper" }[settings.quality] ||
        "Básico";
    const scale = { basic: 1.0, medium: 1.5, high: 2.0 }[settings.quality] || 1.0;
    const clamp = (n) => Math.max(256, Math.min(4096, Math.round(n)));
    const projW = clamp((settings.width || 1024) * scale);
    const projH = clamp((settings.height || 1024) * scale);
    const surcharge = Number(surchargeUsd?.[settings.quality] ?? 0);
    const extras = Math.max(0, refCount - 1) + Math.max(0, apoyoCount - 1);
    const extrasUsd = extras * Number(extraRefCostUsd || 0);
    const baseCop = Number(model?.cost_per_image_cop || 0);
    const trmNow = Number(trm || 4000);
    const totalCop = baseCop + (surcharge + extrasUsd) * trmNow;
    const costLabel = baseCop > 0
        ? currency === "COP"
            ? `${new Intl.NumberFormat("es-CO", {
                  maximumFractionDigits: 0,
              }).format(totalCop)} COP`
            : `$${(trmNow > 0 ? totalCop / trmNow : 0).toFixed(2)} USD`
        : null;
    return (
        <button
            type="button"
            data-testid="chat-config-summary-chip"
            onClick={onClick}
            title="Cambiar modelo, calidad o resolución"
            className="w-full rounded-xl border hairline border-ink-700 hover:border-periwinkle-500/60 bg-ink-900/40 hover:bg-ink-900/70 transition-colors px-4 py-2.5 flex items-center gap-3 flex-wrap text-left group"
        >
            <Settings className="w-3.5 h-3.5 text-fog-400 group-hover:text-periwinkle-800 shrink-0" />
            <span className="font-mono text-[10px] tracking-[0.25em] text-fog-500 uppercase shrink-0">
                Config
            </span>
            <span className="text-fog-100 text-xs truncate min-w-0 flex-1">
                {model?.name || "Modelo"}
            </span>
            <span className="font-mono text-[10px] tabular-nums text-periwinkle-800 shrink-0">
                {qualityLabel}
            </span>
            <span className="font-mono text-[10px] tabular-nums text-fog-400 shrink-0">
                {projW}×{projH}
            </span>
            {settings.num_images > 1 && (
                <span className="font-mono text-[10px] tabular-nums text-amber-300 shrink-0">
                    carrusel ×{settings.num_images}
                </span>
            )}
            {extras > 0 && (
                <span
                    className="font-mono text-[10px] tabular-nums text-amber-400 shrink-0"
                    title={`${extras} imagen${extras === 1 ? "" : "es"} extra · primera ref. y primer apoyo gratis`}
                >
                    +{extras} extra
                </span>
            )}
            {costLabel && (
                <span className="font-mono text-[10px] tabular-nums text-periwinkle-800 shrink-0">
                    {costLabel}
                </span>
            )}
            <ChevronUp className="w-3.5 h-3.5 text-fog-400 group-hover:text-periwinkle-800 shrink-0" />
        </button>
    );
}
